import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Calculator,
  DollarSign,
  CreditCard,
  Wallet,
  Receipt,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Printer,
  Clock,
  Coins,
  Banknote,
  TrendingUp,
  TrendingDown,
  FileText,
  XCircle,
  History,
  Mail,
  Zap,
  Calendar,
  CalendarDays,
  BarChart3,
  Save,
  Download,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import { getPendingOfflineSalesCount } from "@/utils/offlineStorage";
import { syncOfflineSales, isSyncInProgress } from "@/utils/syncService";

// Import refactored components
import { 
  formatCurrency, 
  NOTE_DENOMINATIONS, 
  COIN_DENOMINATIONS,
  SummaryTab,
  ExtendedTab,
  CashCountTab,
  DailyFormTab
} from "@/components/cashup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export const CashUp = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [summaryData, setSummaryData] = useState(null);
  const [existingCashup, setExistingCashup] = useState(null);
  const [activeTab, setActiveTab] = useState("daily");
  
  // Cash count state
  const [noteCounts, setNoteCounts] = useState({});
  const [coinCounts, setCoinCounts] = useState({});
  const [openingFloat, setOpeningFloat] = useState(0);
  
  // Daily Cash-Up Form state (editable fields - green in Excel)
  const [dailyFormKey, setDailyFormKey] = useState(0); // Key to force re-render
  const [existingCashupForDate, setExistingCashupForDate] = useState(null);
  const [dailyForm, setDailyForm] = useState({
    cashUpNo: '',
    pettyCash: 0,
    refunds: 0,
    cashRefunds: 0,
    accountRefunds: 0,
    accPayment: 0,
    // Note counts for daily form
    note200: 0,
    note100: 0,
    note50: 0,
    note20: 0,
    note10: 0,
    // Coins/Change
    change: 0,
    // Speedpoint (Card machine)
    speedpointBatchNo: '',
    speedpointAmount: 0,
  });
  
  // Dialog states
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDailySummaryDialog, setShowDailySummaryDialog] = useState(false);
  const [showMonthlySummaryDialog, setShowMonthlySummaryDialog] = useState(false);
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [cashupHistory, setCashupHistory] = useState([]);
  const [fixingHistoric, setFixingHistoric] = useState(false);
  const [completeNotes, setCompleteNotes] = useState("");

  // Re-order report supplier picker
  const [showReorderPicker, setShowReorderPicker] = useState(false);
  const [reorderData, setReorderData] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  // Suppliers the operator has unticked on the "All suppliers" print
  // path — stored as a Set of `supplier_number || supplier_name` keys
  // (same key scheme as `data-testid` on the picker rows).
  const [reorderExcluded, setReorderExcluded] = useState(() => new Set());
  
  // Email state
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  
  // Finish/Save state
  const [finishEmail, setFinishEmail] = useState("");
  const [savingDaily, setSavingDaily] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  
  // Monthly summary state
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  
  // Monthly Cash Tracking state
  const [monthlyTracking, setMonthlyTracking] = useState(null);
  const [trackingEntries, setTrackingEntries] = useState({});
  const [savingTracking, setSavingTracking] = useState(false);
  
  // Monthly Cash Flow state (Cash In/Out)
  const [cashFlowData, setCashFlowData] = useState(null);
  const [cashFlowEntries, setCashFlowEntries] = useState([]);
  const [savingCashFlow, setSavingCashFlow] = useState(false);
  const [newCashFlowEntry, setNewCashFlowEntry] = useState({ day: 1, reason: '', cash_in: 0, cash_out: 0 });
  
  // Monthly Cash Count state (Physical cash counting)
  const [cashInSafe, setCashInSafe] = useState(0);
  const [fullPackets, setFullPackets] = useState({
    r200: 0, r100: 0, r50: 0, r20: 0, r10: 0
  });
  const [looseNotes, setLooseNotes] = useState({
    r200: 0, r100: 0, r50: 0, r20: 0, r10: 0
  });
  const [savingCashCount, setSavingCashCount] = useState(false);
  
  // Monthly Financial Summary state
  const [financialSummary, setFinancialSummary] = useState(null);
  const [bankBalance, setBankBalance] = useState(0);
  const [suppliersToPay, setSuppliersToPay] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', amount: 0 });
  const [newExpense, setNewExpense] = useState({ name: '', amount: 0 });
  const [suppliersList, setSuppliersList] = useState([]);

  // Print refs
  const printRef = useRef(null);
  const dailyPrintRef = useRef(null);
  const monthlyPrintRef = useRef(null);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      // Safety net — flush any still-pending offline sales BEFORE we
      // read the day's totals. The till can accumulate unsynced sales
      // if the backend was briefly unreachable mid-shift; without this
      // pre-sync the cash-up summary would under-count the day and
      // the drawer would come up short at count time (the exact bug
      // the user reported). Only runs when online + pending > 0, and
      // the shared syncService guards against concurrent runs.
      try {
        if (navigator.onLine && !isSyncInProgress()) {
          const pending = await getPendingOfflineSalesCount();
          if (pending > 0) {
            toast.info(`Syncing ${pending} offline sale${pending > 1 ? 's' : ''} before cash-up…`);
            const r = await syncOfflineSales({ autoMode: true });
            if (r?.success && r?.synced > 0) {
              toast.success(`Synced ${r.synced} sale${r.synced > 1 ? 's' : ''} — totals now include them`);
            } else if (r && r.failed > 0) {
              toast.error(`${r.failed} sale${r.failed > 1 ? 's' : ''} failed to sync — totals may be short`, { duration: 10000 });
            }
          }
        }
      } catch (_e) {
        // Best-effort — never block cash-up load on a sync problem.
      }

      // First check if a daily cashup exists for this date
      const checkResponse = await axios.get(`${API}/cashup/daily/check`, {
        params: { date: selectedDate }
      });
      
      // Always fetch the actual summary (even if cash-up was done)
      const response = await axios.get(`${API}/cashup/summary`, {
        params: { date: selectedDate }
      });
      setSummaryData(response.data.summary);
      
      // Pre-fill the daily form with system-calculated values
      const summary = response.data.summary;
      setDailyForm(prev => ({
        ...prev,
        pettyCash: summary?.petty_cash || 0,
        refunds: summary?.refunds || 0,
        cashRefunds: summary?.cash_refunds || 0,
        accountRefunds: summary?.account_refunds || 0,
        accPayment: summary?.account_receipts || 0,
        // Pre-fill speedpoint with card sales + card portion from account receipts
        speedpointAmount: (summary?.card_sales || 0) + (summary?.account_receipts_card || 0),
        // Auto-generate cash-up number
        cashUpNo: response.data.next_cashup_no ? String(response.data.next_cashup_no) : '',
      }));
      
      // If cash-up already exists for this date, load the SAVED values (read-only view)
      if (checkResponse.data.exists) {
        setExistingCashup(null);
        setExistingCashupForDate(checkResponse.data.cashup);
        
        // Load saved cashup values into the form so user sees what was submitted
        const saved = checkResponse.data.cashup;
        if (saved) {
          // Backend persists cash counts under `form_data.note_200` etc. (see
          // routes/cashup.py /cashup/daily/save). Earlier code looked for a
          // `cash_count.notes/coins` structure that doesn't exist — so the
          // counts never came back and a completed cash-up appeared as zeros
          // with a misleading FINAL BALANCE / OVER amount.
          const fd = saved.form_data || {};
          setDailyForm(prev => ({
            ...prev,
            pettyCash: saved.petty_cash ?? fd.petty_cash ?? summary?.petty_cash ?? 0,
            refunds: saved.refunds ?? fd.refunds ?? summary?.refunds ?? 0,
            cashRefunds: saved.cash_refunds ?? fd.cash_refunds ?? summary?.cash_refunds ?? 0,
            accountRefunds: saved.account_refunds ?? fd.account_refunds ?? summary?.account_refunds ?? 0,
            accPayment: saved.acc_payment ?? fd.acc_payment ?? summary?.account_receipts ?? 0,
            speedpointAmount: saved.speedpoint_amount ?? fd.speedpoint_amount ?? 0,
            speedpointBatchNo: saved.speedpoint_batch_no ?? fd.speedpoint_batch_no ?? '',
            cashUpNo: saved.cashup_no ? String(saved.cashup_no) : prev.cashUpNo,
            note200: fd.note_200 ?? saved.note_200 ?? 0,
            note100: fd.note_100 ?? saved.note_100 ?? 0,
            note50: fd.note_50 ?? saved.note_50 ?? 0,
            note20: fd.note_20 ?? saved.note_20 ?? 0,
            note10: fd.note_10 ?? saved.note_10 ?? 0,
            change: fd.change ?? saved.change ?? 0,
          }));
          setDailyFormKey(prev => prev + 1);
          
          // Load saved cash count if available (legacy structure — kept for
          // any older records that did persist a `cash_count` object).
          if (saved.cash_count) {
            const notes = {};
            const coins = {};
            NOTE_DENOMINATIONS.forEach(d => { notes[d] = 0; });
            COIN_DENOMINATIONS.forEach(d => { coins[d] = 0; });
            saved.cash_count.notes?.forEach(n => { notes[n.denomination] = n.count; });
            saved.cash_count.coins?.forEach(c => { coins[c.denomination] = c.count; });
            setNoteCounts(notes);
            setCoinCounts(coins);
          } else {
            // Mirror the loaded denomination counts into noteCounts so any
            // component reading from that map (cash count table, totals)
            // also reflects the saved values rather than zeros.
            const notes = {};
            const coins = {};
            NOTE_DENOMINATIONS.forEach(d => { notes[d] = 0; });
            COIN_DENOMINATIONS.forEach(d => { coins[d] = 0; });
            if (NOTE_DENOMINATIONS.includes(200)) notes[200] = fd.note_200 ?? 0;
            if (NOTE_DENOMINATIONS.includes(100)) notes[100] = fd.note_100 ?? 0;
            if (NOTE_DENOMINATIONS.includes(50)) notes[50] = fd.note_50 ?? 0;
            if (NOTE_DENOMINATIONS.includes(20)) notes[20] = fd.note_20 ?? 0;
            if (NOTE_DENOMINATIONS.includes(10)) notes[10] = fd.note_10 ?? 0;
            setNoteCounts(notes);
            setCoinCounts(coins);
          }
        }
      } else {
        // No cash-up exists, normal flow
        setExistingCashup(response.data.existing_cashup);
        setExistingCashupForDate(null);
        
        // If existing cashup has cash count, load it
        if (response.data.existing_cashup?.cash_count) {
          const cashCount = response.data.existing_cashup.cash_count;
          const notes = {};
          const coins = {};
          cashCount.notes?.forEach(n => { notes[n.denomination] = n.count; });
          cashCount.coins?.forEach(c => { coins[c.denomination] = c.count; });
          setNoteCounts(notes);
          setCoinCounts(coins);
        } else {
          // Reset counts
          const notes = {};
          const coins = {};
          NOTE_DENOMINATIONS.forEach(d => { notes[d] = 0; });
          COIN_DENOMINATIONS.forEach(d => { coins[d] = 0; });
          setNoteCounts(notes);
          setCoinCounts(coins);
        }
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Failed to load cash-up summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/cashup/daily/history`);
      setCashupHistory(response.data.records || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  useEffect(() => {
    fetchSummary();
    checkExistingDailyCashup();
    // Reset form when date changes
    setDailyForm({
      cashUpNo: '',
      pettyCash: 0,
      refunds: 0,
      cashRefunds: 0,
      accountRefunds: 0,
      accPayment: 0,
      note200: 0,
      note100: 0,
      note50: 0,
      note20: 0,
      note10: 0,
      change: 0,
      speedpointBatchNo: '',
      speedpointAmount: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Auto-populate refunds and petty cash from system data
  useEffect(() => {
    if (summaryData) {
      setDailyForm(prev => ({
        ...prev,
        refunds: summaryData.refunds || 0,
        cashRefunds: summaryData.cash_refunds || 0,
        accountRefunds: summaryData.account_refunds || 0,
        pettyCash: summaryData.petty_cash || 0,
        accPayment: summaryData.account_receipts || 0  // Total account receipts, not just cash portion
      }));
    }
  }, [summaryData]);

  const handleStartCashup = async () => {
    try {
      const response = await axios.post(`${API}/cashup/create`, {
        cashup_date: selectedDate,
        terminal: "ALL",
        opening_float: parseFloat(openingFloat) || 0,
        created_by: "ADMIN"
      });
      
      toast.success("Cash-up started successfully");
      setShowStartDialog(false);
      setExistingCashup(response.data.cashup);
      fetchSummary();
    } catch (error) {
      console.error("Error starting cash-up:", error);
      toast.error(error.response?.data?.detail || "Failed to start cash-up");
    }
  };

  // Quick Cash-Up (bypass cash count)
  const handleQuickCashup = async () => {
    try {
      const response = await axios.post(`${API}/cashup/quick`, {
        cashup_date: selectedDate,
        terminal: "ALL",
        created_by: "ADMIN",
        notes: "Quick cash-up"
      });
      
      toast.success("Quick cash-up completed!");
      setExistingCashup(response.data.cashup);
      fetchSummary();
      
      // Auto-print after quick cash-up
      setTimeout(() => handlePrint(), 500);
    } catch (error) {
      console.error("Error with quick cash-up:", error);
      toast.error(error.response?.data?.detail || "Failed to complete quick cash-up");
    }
  };

  // Check if a daily cashup already exists for the selected date
  const checkExistingDailyCashup = async () => {
    try {
      const response = await axios.get(`${API}/cashup/daily/check`, {
        params: { date: selectedDate }
      });
      const exists = response.data.exists;
      setExistingCashupForDate(exists ? response.data.cashup : null);
      
      if (exists) {
        // Cashup already finalized for this date — reset form
        setDailyForm({
          cashUpNo: '',
          pettyCash: 0,
          refunds: 0,
          cashRefunds: 0,
          accountRefunds: 0,
          accPayment: 0,
          note200: 0,
          note100: 0,
          note50: 0,
          note20: 0,
          note10: 0,
          change: 0,
          speedpointBatchNo: '',
          speedpointAmount: 0,
        });
        setDailyFormKey(prev => prev + 1);
      } else if (response.data.draft) {
        // Load saved draft
        const d = response.data.draft;
        setDailyForm(prev => ({
          ...prev,
          cashUpNo: d.cashup_no || prev.cashUpNo,
          pettyCash: d.petty_cash ?? prev.pettyCash,
          refunds: d.refunds ?? prev.refunds,
          cashRefunds: d.cash_refunds ?? prev.cashRefunds,
          accountRefunds: d.account_refunds ?? prev.accountRefunds,
          accPayment: d.acc_payment ?? prev.accPayment,
          note200: d.note_200 ?? 0,
          note100: d.note_100 ?? 0,
          note50: d.note_50 ?? 0,
          note20: d.note_20 ?? 0,
          note10: d.note_10 ?? 0,
          change: d.change ?? 0,
          speedpointBatchNo: d.speedpoint_batch_no || '',
          speedpointAmount: d.speedpoint_amount ?? prev.speedpointAmount,
        }));
        setDailyFormKey(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error checking existing cashup:", error);
      setExistingCashupForDate(null);
    }
  };

  // Fetch monthly summary
  const fetchMonthlySummary = async () => {
    setLoadingMonthly(true);
    try {
      const response = await axios.get(`${API}/cashup/monthly-summary`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setMonthlySummary(response.data.summary);
    } catch (error) {
      // Silent — background enrichment, don't scare the cashier on blips.
      console.warn("Monthly summary load failed:", error.message);
    } finally {
      setLoadingMonthly(false);
    }
  };

  // Fetch monthly cash tracking data
  const fetchMonthlyTracking = async () => {
    setLoadingMonthly(true);
    try {
      const response = await axios.get(`${API}/cashup/monthly-tracking`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setMonthlyTracking(response.data);
      
      // Initialize tracking entries from fetched data
      const entries = {};
      response.data.entries?.forEach(entry => {
        entries[entry.day] = {
          cash_sales: entry.cash_sales || 0,
          card_sales: entry.card_sales || 0,
          acc_sales: entry.acc_sales || 0,
          cash_counted: entry.cash_counted || 0,
        };
      });
      setTrackingEntries(entries);
      
      // Also fetch cash flow, cash count, and financial summary data
      fetchMonthlyCashFlow();
      fetchMonthlyCashCount();
      fetchMonthlyFinancialSummary();
      fetchSuppliersList();
    } catch (error) {
      // Silent — background enrichment.
      console.warn("Monthly tracking load failed:", error.message);
      toast.error("Failed to load monthly tracking");
    } finally {
      setLoadingMonthly(false);
    }
  };

  // Save monthly cash tracking data
  const handleSaveMonthlyTracking = async () => {
    setSavingTracking(true);
    try {
      const entries = Object.keys(trackingEntries).map(day => ({
        day: parseInt(day),
        cash_sales: trackingEntries[day]?.cash_sales || 0,
        card_sales: trackingEntries[day]?.card_sales || 0,
        acc_sales: trackingEntries[day]?.acc_sales || 0,
        cash_counted: trackingEntries[day]?.cash_counted || 0,
      }));
      
      await saveWithRetry(() => axios.post(`${API}/cashup/monthly-tracking/save`, {
        year: selectedYear,
        month: selectedMonth,
        entries
      }));
      
      toast.success("Monthly tracking saved successfully!");
      fetchMonthlyTracking(); // Refresh data
    } catch (error) {
      console.error("Error saving monthly tracking:", error);
      toast.error(errorMessage(error, "Failed to save monthly tracking"));
    } finally {
      setSavingTracking(false);
    }
  };

  // Export monthly tracking to Excel
  const handleExportMonthlyTracking = () => {
    const url = `${API}/cashup/monthly-tracking/export?year=${selectedYear}&month=${selectedMonth}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `MonthlyCashTracking_${selectedYear}_${selectedMonth}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Excel file downloading...");
  };

  // Update a single day's tracking entry
  const updateTrackingEntry = (day, field, value) => {
    setTrackingEntries(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Fetch monthly cash flow data
  const fetchMonthlyCashFlow = async () => {
    try {
      const response = await axios.get(`${API}/cashup/monthly-cashflow`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setCashFlowData(response.data);
      setCashFlowEntries(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching cash flow:", error);
    }
  };

  // Save monthly cash flow data
  const handleSaveCashFlow = async () => {
    setSavingCashFlow(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/cashup/monthly-cashflow/save`, {
        year: selectedYear,
        month: selectedMonth,
        entries: cashFlowEntries
      }));
      toast.success("Cash flow saved successfully!");
      fetchMonthlyCashFlow();
    } catch (error) {
      console.error("Error saving cash flow:", error);
      toast.error(errorMessage(error, "Failed to save cash flow"));
    } finally {
      setSavingCashFlow(false);
    }
  };

  // Add new cash flow entry
  const handleAddCashFlowEntry = () => {
    if (!newCashFlowEntry.reason.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    if (newCashFlowEntry.cash_in === 0 && newCashFlowEntry.cash_out === 0) {
      toast.error("Please enter a Cash In or Cash Out amount");
      return;
    }
    
    const newEntry = {
      ...newCashFlowEntry,
      id: Date.now().toString()
    };
    
    setCashFlowEntries(prev => [...prev, newEntry]);
    setNewCashFlowEntry({ day: 1, reason: '', cash_in: 0, cash_out: 0 });
    toast.success("Entry added - click Save to persist");
  };

  // Delete cash flow entry — hits the backend DELETE endpoint IMMEDIATELY
  // so removal is explicit and audited. Never rely on "save to persist" here:
  // the save endpoint now merges by id and can no longer wipe entries by
  // omission, so a local-only remove would just re-appear after the next fetch.
  const handleDeleteCashFlowEntry = async (entryId) => {
    if (!entryId) return;
    try {
      await axios.delete(`${API}/cashup/monthly-cashflow/entry/${entryId}`, {
        params: { year: selectedYear, month: selectedMonth },
      });
      setCashFlowEntries(prev => prev.filter(e => e.id !== entryId));
      toast.success("Entry removed");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to remove entry"));
    }
  };

  // Fetch monthly cash count data
  const fetchMonthlyCashCount = async () => {
    try {
      const response = await axios.get(`${API}/cashup/monthly-cashcount`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setCashInSafe(response.data.cash_in_safe || 0);
      setFullPackets(response.data.full_packets || { r200: 0, r100: 0, r50: 0, r20: 0, r10: 0 });
      setLooseNotes(response.data.loose_notes || { r200: 0, r100: 0, r50: 0, r20: 0, r10: 0 });
    } catch (error) {
      console.error("Error fetching cash count:", error);
    }
  };

  // Save monthly cash count data
  const handleSaveCashCount = async () => {
    setSavingCashCount(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/cashup/monthly-cashcount/save`, {
        year: selectedYear,
        month: selectedMonth,
        cash_in_safe: cashInSafe,
        full_packets: fullPackets,
        loose_notes: looseNotes
      }));
      toast.success("Cash count saved successfully!");
    } catch (error) {
      console.error("Error saving cash count:", error);
      toast.error(errorMessage(error, "Failed to save cash count"));
    } finally {
      setSavingCashCount(false);
    }
  };

  // Calculate cash count totals
  const calculateCashCountTotals = () => {
    // Full packets: each packet = 10 notes
    const fullPacketsTotal = 
      (fullPackets.r200 || 0) * 200 * 10 +  // R200 x 10 = R2000 per packet
      (fullPackets.r100 || 0) * 100 * 10 +  // R100 x 10 = R1000 per packet
      (fullPackets.r50 || 0) * 50 * 10 +    // R50 x 10 = R500 per packet
      (fullPackets.r20 || 0) * 20 * 10 +    // R20 x 10 = R200 per packet
      (fullPackets.r10 || 0) * 10 * 10;     // R10 x 10 = R100 per packet
    
    // Loose notes: individual notes
    const looseNotesTotal = 
      (looseNotes.r200 || 0) * 200 +
      (looseNotes.r100 || 0) * 100 +
      (looseNotes.r50 || 0) * 50 +
      (looseNotes.r20 || 0) * 20 +
      (looseNotes.r10 || 0) * 10;
    
    const totalCounted = fullPacketsTotal + looseNotesTotal;
    const variance = totalCounted - cashInSafe;
    
    return { fullPacketsTotal, looseNotesTotal, totalCounted, variance };
  };

  // Fetch suppliers list
  const fetchSuppliersList = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      // Sort suppliers alphabetically by name
      const sortedSuppliers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setSuppliersList(sortedSuppliers);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  // Fetch monthly financial summary data
  const fetchMonthlyFinancialSummary = async () => {
    try {
      const response = await axios.get(`${API}/cashup/monthly-financial-summary`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setFinancialSummary(response.data);
      setBankBalance(response.data.bank_balance || 0);
      setSuppliersToPay(response.data.suppliers_to_pay || []);
      setMonthlyExpenses(response.data.monthly_expenses || []);
    } catch (error) {
      console.error("Error fetching financial summary:", error);
    }
  };

  // Save monthly financial summary
  const handleSaveFinancialSummary = async () => {
    setSavingFinancial(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/cashup/monthly-financial-summary/save`, {
        year: selectedYear,
        month: selectedMonth,
        bank_balance: bankBalance,
        suppliers_to_pay: suppliersToPay,
        monthly_expenses: monthlyExpenses
      }));
      toast.success("Financial summary saved successfully!");
      fetchMonthlyFinancialSummary();
    } catch (error) {
      console.error("Error saving financial summary:", error);
      toast.error(errorMessage(error, "Failed to save financial summary"));
    } finally {
      setSavingFinancial(false);
    }
  };

  // Add supplier to pay
  const handleAddSupplier = () => {
    if (!newSupplier.name.trim()) {
      toast.error("Please enter a supplier name");
      return;
    }
    setSuppliersToPay(prev => [...prev, { ...newSupplier, id: Date.now().toString() }]);
    setNewSupplier({ name: '', amount: 0 });
  };

  // Remove supplier
  const handleRemoveSupplier = (id) => {
    setSuppliersToPay(prev => prev.filter(s => s.id !== id));
  };

  // Add monthly expense
  const handleAddExpense = () => {
    if (!newExpense.name.trim()) {
      toast.error("Please enter an expense name");
      return;
    }
    setMonthlyExpenses(prev => [...prev, { ...newExpense, id: Date.now().toString() }]);
    setNewExpense({ name: '', amount: 0 });
  };

  // Remove expense
  const handleRemoveExpense = (id) => {
    setMonthlyExpenses(prev => prev.filter(e => e.id !== id));
  };

  // Toggle supplier paid status
  const handleToggleSupplierPaid = (id) => {
    setSuppliersToPay(prev => prev.map(s => 
      s.id === id ? { ...s, paid: !s.paid, paid_amount: !s.paid ? s.amount : 0 } : s
    ));
  };

  // Handle partial payment for supplier
  const handleSupplierPartialPayment = (id, paidAmount) => {
    setSuppliersToPay(prev => prev.map(s => {
      if (s.id !== id) return s;
      const amount = parseFloat(paidAmount) || 0;
      return { 
        ...s, 
        paid_amount: amount,
        paid: amount >= s.amount // Fully paid if paid_amount >= amount
      };
    }));
  };

  // Toggle expense paid status
  const handleToggleExpensePaid = (id) => {
    setMonthlyExpenses(prev => prev.map(e => 
      e.id === id ? { ...e, paid: !e.paid } : e
    ));
  };

  // Calculate financial totals
  const calculateFinancialTotals = () => {
    const cashInSafeAmount = calculateCashCountTotals().totalCounted;
    const totalCashAvailable = cashInSafeAmount + bankBalance;
    const totalSuppliers = suppliersToPay.reduce((sum, s) => sum + (s.amount || 0), 0);
    const totalExpenses = monthlyExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    // Calculate unpaid amounts (amount - paid_amount for partial payments)
    const unpaidSuppliers = suppliersToPay.reduce((sum, s) => {
      const paidAmount = s.paid_amount || (s.paid ? s.amount : 0);
      return sum + Math.max(0, (s.amount || 0) - paidAmount);
    }, 0);
    const unpaidExpenses = monthlyExpenses.filter(e => !e.paid).reduce((sum, e) => sum + (e.amount || 0), 0);
    const totalToPay = unpaidSuppliers + unpaidExpenses;
    // Calculate paid amounts
    const paidSuppliers = suppliersToPay.reduce((sum, s) => sum + (s.paid_amount || (s.paid ? s.amount : 0)), 0);
    const paidExpenses = monthlyExpenses.filter(e => e.paid).reduce((sum, e) => sum + (e.amount || 0), 0);
    const totalPaid = paidSuppliers + paidExpenses;
    const netPosition = totalCashAvailable - totalToPay;
    
    return { cashInSafeAmount, totalCashAvailable, totalSuppliers, totalExpenses, totalToPay, totalPaid, netPosition };
  };

  // Email cash-up
  const handleSendEmail = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setEmailSending(true);
    try {
      // Build email HTML content
      const htmlContent = `
        <html>
          <body style="font-family: Arial, sans-serif; padding: 20px;">
            <h1 style="text-align: center;">Cash-Up Report</h1>
            <p style="text-align: center;">Date: ${selectedDate}</p>
            <p style="text-align: center;">Terminal: ALL</p>
            <hr/>
            <h2>Sales Summary</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr><td>Total Sales:</td><td style="text-align: right; font-weight: bold;">${formatCurrency(summaryData?.total_sales)}</td></tr>
              <tr><td>Tax Portion:</td><td style="text-align: right;">${formatCurrency(summaryData?.tax_portion)}</td></tr>
              <tr><td>Nett Sales:</td><td style="text-align: right;">${formatCurrency(summaryData?.nett_sales)}</td></tr>
              <tr><td>Profit:</td><td style="text-align: right; color: green;">${formatCurrency(summaryData?.profit)}</td></tr>
              <tr><td>GP%:</td><td style="text-align: right;">${(summaryData?.gp_percent || 0).toFixed(2)}%</td></tr>
            </table>
            <h2>Payment Breakdown</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr><td>Cash Sales:</td><td style="text-align: right;">${formatCurrency(summaryData?.cash_sales)}</td></tr>
              <tr><td>Card Sales:</td><td style="text-align: right;">${formatCurrency(summaryData?.card_sales)}</td></tr>
              <tr><td>Account Sales:</td><td style="text-align: right;">${formatCurrency(summaryData?.account_sales)}</td></tr>
              <tr><td>EFT Sales:</td><td style="text-align: right;">${formatCurrency(summaryData?.eft_sales)}</td></tr>
            </table>
            <h2>Other</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr><td>Account Receipts:</td><td style="text-align: right;">${formatCurrency(summaryData?.account_receipts)}</td></tr>
              <tr><td>Petty Cash:</td><td style="text-align: right;">-${formatCurrency(summaryData?.petty_cash)}</td></tr>
              <tr><td>Discounts:</td><td style="text-align: right;">-${formatCurrency(summaryData?.discounts)}</td></tr>
              <tr><td>Transactions:</td><td style="text-align: right;">${summaryData?.transactions || 0}</td></tr>
            </table>
            <hr/>
            <p style="text-align: center; font-size: 12px; color: #666;">Generated on ${new Date().toLocaleString()}</p>
          </body>
        </html>
      `;
      
      await axios.post(`${API}/email/send`, {
        to_email: emailTo,
        subject: `Cash-Up Report - ${selectedDate}`,
        body: htmlContent
      });
      
      toast.success("Email sent successfully!");
      setShowEmailDialog(false);
      setEmailTo("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  // Print Daily Summary
  const handlePrintDaily = () => {
    const printContent = dailyPrintRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Daily Summary - ${selectedDate}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 18px; text-align: center; margin-bottom: 5px; }
            h2 { font-size: 14px; margin-top: 15px; border-bottom: 1px solid #000; }
            .header { text-align: center; margin-bottom: 20px; }
            .row { display: flex; justify-content: space-between; padding: 3px 0; }
            .row.highlight { background: #f0f0f0; font-weight: bold; }
            .section { margin-bottom: 15px; }
            .total-row { border-top: 2px solid #000; font-weight: bold; margin-top: 10px; padding-top: 5px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Print Monthly Summary
  const handlePrintMonthly = () => {
    const printContent = monthlyPrintRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Monthly Summary - ${monthlySummary?.month_name} ${selectedYear}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 18px; text-align: center; margin-bottom: 5px; }
            h2 { font-size: 14px; margin-top: 15px; border-bottom: 1px solid #000; }
            .header { text-align: center; margin-bottom: 20px; }
            .row { display: flex; justify-content: space-between; padding: 3px 0; }
            .section { margin-bottom: 15px; }
            .total-row { border-top: 2px solid #000; font-weight: bold; margin-top: 10px; padding-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 10px; }
            th { background: #f5f5f5; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Save draft (without finalizing)
  const handleSaveDraft = async () => {
    if (existingCashupForDate) {
      toast.error(`Cash-up already finalized for ${selectedDate}`);
      return;
    }
    setSavingDraft(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/cashup/daily/draft`, {
        date: selectedDate,
        cashup_no: dailyForm.cashUpNo,
        petty_cash: dailyForm.pettyCash,
        refunds: dailyForm.refunds,
        cash_refunds: dailyForm.cashRefunds,
        account_refunds: dailyForm.accountRefunds,
        acc_payment: dailyForm.accPayment,
        note_200: dailyForm.note200,
        note_100: dailyForm.note100,
        note_50: dailyForm.note50,
        note_20: dailyForm.note20,
        note_10: dailyForm.note10,
        change: dailyForm.change,
        speedpoint_batch_no: dailyForm.speedpointBatchNo,
        speedpoint_amount: dailyForm.speedpointAmount,
      }));
      toast.success("Draft saved");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error(errorMessage(error, "Failed to save draft"));
    } finally {
      setSavingDraft(false);
    }
  };

  // Save and Finish Daily Cash-Up
  const handleFinishDailyCashup = async () => {
    // Check if a cashup already exists for this date
    if (existingCashupForDate) {
      toast.error(`A cash-up already exists for ${selectedDate}. Only ONE cash-up per day is allowed.`);
      setShowFinishDialog(false);
      return;
    }
    
    setSavingDaily(true);
    try {
      const response = await saveWithRetry(() => axios.post(`${API}/cashup/daily/save`, {
        date: selectedDate,
        cashup_no: dailyForm.cashUpNo,
        petty_cash: dailyForm.pettyCash,
        refunds: dailyForm.refunds,
        cash_refunds: dailyForm.cashRefunds,
        account_refunds: dailyForm.accountRefunds,
        acc_payment: dailyForm.accPayment,
        note_200: dailyForm.note200,
        note_100: dailyForm.note100,
        note_50: dailyForm.note50,
        note_20: dailyForm.note20,
        note_10: dailyForm.note10,
        change: dailyForm.change,
        speedpoint_batch_no: dailyForm.speedpointBatchNo,
        speedpoint_amount: dailyForm.speedpointAmount,
        email_to: finishEmail || null
      }));
      
      if (response.data.success) {
        toast.success("Daily cash-up saved successfully!");
        
        // Close dialog
        setShowFinishDialog(false);
        setFinishEmail("");
        
        // Auto-download Excel file FIRST (before date changes)
        const recordId = response.data.record_id;
        const excelUrl = `${API}/cashup/daily/export/${recordId}`;
        const currentDateForFile = selectedDate;
        
        setTimeout(() => {
          const link = document.createElement('a');
          link.href = excelUrl;
          link.download = `CashUp_${currentDateForFile}_${response.data.record?.cashup_no || 'N'}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("Excel file downloaded!");
        }, 200);
        
        if (response.data.email_sent) {
          toast.success(`Email sent to ${finishEmail}`);
        }
        
        // ADVANCE TO NEXT DAY - This clears everything for a fresh start
        const currentDate = new Date(selectedDate);
        currentDate.setDate(currentDate.getDate() + 1);
        const nextDate = currentDate.toISOString().split('T')[0];
        
        // Clear all states immediately
        setSummaryData(null);
        setExistingCashup(null);
        setExistingCashupForDate(null);
        
        // Reset form
        setDailyForm({
          cashUpNo: '',
          pettyCash: 0,
          refunds: 0,
          cashRefunds: 0,
          accountRefunds: 0,
          accPayment: 0,
          note200: 0,
          note100: 0,
          note50: 0,
          note20: 0,
          note10: 0,
          change: 0,
          speedpointBatchNo: '',
          speedpointAmount: 0,
        });
        
        // Force re-render
        setDailyFormKey(prev => prev + 1);
        
        // Now change the date - this will trigger useEffect to fetch fresh data (which will be empty/zeros for next day)
        setSelectedDate(nextDate);
        
        toast.info(`Ready for next day: ${nextDate}`, { duration: 5000 });
      }
    } catch (error) {
      console.error("Error saving daily cash-up:", error);
      toast.error(errorMessage(error, "Failed to save daily cash-up"));
    } finally {
      setSavingDaily(false);
    }
  };

  const calculateTotalCounted = () => {
    let total = 0;
    NOTE_DENOMINATIONS.forEach(d => {
      total += (noteCounts[d] || 0) * d;
    });
    COIN_DENOMINATIONS.forEach(d => {
      total += (coinCounts[d] || 0) * d;
    });
    return total;
  };

  // Helper: escape HTML for safe injection into the print template
  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  /**
   * Re-order report — prints products at or below reorder level (after
   * subtracting stock_on_order), grouped by supplier. Compact two-column
   * layout to keep page count down. Auto-fires after cash-up completion
   * and can also be triggered manually from the page header.
   *
   * @param {Object} opts
   * @param {boolean} opts.silent — if true, suppress the "nothing to print" toast
   *   so a quiet day doesn't bother the user at cash-up time.
   * @param {Object}  opts.data — pre-fetched report payload (avoids a second
   *   API call when the picker already loaded it).
   * @param {string}  opts.supplierKey — if set, only print the supplier whose
   *   `supplier_number` (preferred) or `supplier_name` matches this key.
   */
  const printReorderReport = async ({ silent = false, data = null, supplierKey = null, excludedKeys = null } = {}) => {
    let payload = data;
    if (!payload) {
      try {
        const res = await axios.get(`${API}/reports/below-reorder-level`, {
          params: { only_unfilled: true },
        });
        payload = res.data;
      } catch (e) {
        console.error("reorder report fetch failed", e);
        if (!silent) toast.error("Could not load re-order report");
        return;
      }
    }
    if (!payload || !payload.suppliers || payload.total_items === 0) {
      if (!silent) toast.info("No products are below reorder level — nothing to print.");
      return;
    }

    // Apply optional supplier filter
    let suppliers = payload.suppliers;
    let titleSuffix = "";
    if (supplierKey) {
      suppliers = suppliers.filter((s) =>
        (s.supplier_number && String(s.supplier_number) === String(supplierKey)) ||
        (s.supplier_name && s.supplier_name === supplierKey)
      );
      if (suppliers.length === 0) {
        if (!silent) toast.info("No items below reorder level for that supplier.");
        return;
      }
      titleSuffix = ` — ${suppliers[0].supplier_name || "(No supplier)"}`;
    } else if (excludedKeys && excludedKeys.size > 0) {
      // "All suppliers" print path with some suppliers unticked — drop
      // them before rendering. Key uses the same `supplier_number ||
      // supplier_name` convention as the picker rows so the two always
      // agree.
      const before = suppliers.length;
      suppliers = suppliers.filter((s) => {
        const key = s.supplier_number || s.supplier_name || "(no-supplier)";
        return !excludedKeys.has(key);
      });
      if (suppliers.length === 0) {
        if (!silent) toast.info("All suppliers were excluded — nothing to print.");
        return;
      }
      const skipped = before - suppliers.length;
      if (skipped > 0) {
        titleSuffix = ` — ${suppliers.length} supplier${suppliers.length === 1 ? "" : "s"} (${skipped} excluded)`;
      }
    }
    const totalItems = suppliers.reduce((sum, s) => sum + (s.items?.length || 0), 0);

    const printedAt = new Date().toLocaleString("en-ZA");
    const sections = suppliers
      .map((sup) => {
        const rows = sup.items
          .map(
            (it) => `<tr>
              <td class="plu">${escapeHtml(it.plu_code || "—")}</td>
              <td class="imp imp${it.order_importance || 0}">${it.order_importance || ""}</td>
              <td class="splu">${escapeHtml(it.supplier_plu || "—")}</td>
              <td class="bc">${escapeHtml(it.barcode || "—")}</td>
              <td class="desc">${escapeHtml(it.name || "")}</td>
              <td class="num">${Number(it.stock_qty || 0)}</td>
              <td class="num">${Number(it.reorder_level || 0)}</td>
              <td class="num">${Number(it.stock_on_order || 0)}</td>
              <td class="num sold">${Number(it.qty_sold_1m || 0)}</td>
              <td class="num sold">${Number(it.qty_sold_6m || 0)}</td>
              <td class="num need">${Number(it.net_needed || 0)}</td>
            </tr>`
          )
          .join("");
        const supLabel = sup.supplier_name || "(No supplier assigned)";
        const supNum = sup.supplier_number ? ` · ${escapeHtml(sup.supplier_number)}` : "";
        return `
          <section class="sup-block">
            <h2>${escapeHtml(supLabel)}${supNum}
              <span class="count">${sup.items.length} item${sup.items.length === 1 ? "" : "s"}</span>
            </h2>
            <table>
              <thead>
                <tr>
                  <th class="plu">PLU</th>
                  <th class="imp" title="Order importance: 1 = very important">Imp</th>
                  <th class="splu">Sup PLU</th>
                  <th class="bc">Barcode</th>
                  <th class="desc">Description</th>
                  <th class="num">Stock</th>
                  <th class="num">R/Lvl</th>
                  <th class="num">O/Ord</th>
                  <th class="num">Sold 1m</th>
                  <th class="num">Sold 6m</th>
                  <th class="num">Order</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </section>
        `;
      })
      .join("");
    const win = window.open("", "_blank", "width=1000,height=1200");
    if (!win) {
      if (!silent) toast.error("Pop-up blocked. Allow pop-ups for this site to print.");
      return;
    }
    win.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>Re-order Report${escapeHtml(titleSuffix)} — ${escapeHtml(printedAt)}</title>
  <style>
    @page { size: A4; margin: 8mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 10px; margin: 0; }
    h1 { font-size: 16px; margin: 0 0 2px 0; letter-spacing: 0.5px; }
    .sub { font-size: 10px; color: #555; margin-bottom: 6px; }
    .summary { display: flex; gap: 14px; font-size: 10px; background: #f5f5f5; padding: 5px 8px; border-radius: 4px; margin-bottom: 10px; }
    .summary b { font-size: 12px; color: #000; }
    /* Single-column layout to give the new sales/supplier columns room.
       (Previously two-column when "All suppliers" was selected.) */
    .columns { column-count: 1; }
    .sup-block { break-inside: avoid; page-break-inside: avoid; margin-bottom: 8px; }
    .sup-block h2 { font-size: 11px; margin: 0 0 2px 0; padding: 3px 6px; background: #2b2b2b; color: #fff; border-radius: 3px; display: flex; justify-content: space-between; align-items: center; }
    .sup-block h2 .count { font-size: 9px; font-weight: normal; color: #d0d0d0; }
    /* table-layout: fixed + explicit column widths prevents description text
       from pushing the trailing numeric columns off the printable area. */
    table { width: 100%; border-collapse: collapse; margin-bottom: 4px; table-layout: fixed; }
    th, td { border-bottom: 1px solid #eee; padding: 2px 3px; text-align: left; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; }
    th { background: #fafafa; font-size: 8px; text-transform: uppercase; letter-spacing: 0.2px; color: #555; }
    td.plu, th.plu { font-family: 'Courier New', monospace; font-weight: bold; width: 38px; font-size: 9px; }
    td.splu, th.splu { font-family: 'Courier New', monospace; width: 60px; font-size: 9px; color: #444; }
    td.bc, th.bc { font-family: 'Courier New', monospace; width: 80px; font-size: 8.5px; color: #444; }
    td.desc, th.desc { font-size: 9px; }
    td.imp, th.imp { width: 22px; text-align: center; font-weight: bold; font-size: 9px; }
    td.imp1 { background: #dc2626; color: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; outline: 1.5px solid #000; outline-offset: -2px; }
    td.imp2 { background: #fef3c7; color: #92400e; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    td.imp3 { color: #64748b; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; width: 38px; }
    td.sold { color: #155e75; font-weight: 600; }
    td.need { background: #fff3cd; font-weight: bold; color: #92400e; }
    @media print { .no-print { display: none; } }
  </style>
</head>
<body>
  <h1>RE-ORDER REPORT${escapeHtml(titleSuffix)}</h1>
  <div class="sub">Generated ${escapeHtml(printedAt)} · Items below reorder level after deducting stock on order</div>
  <div class="summary">
    <div><b>${totalItems}</b> products need ordering</div>
    <div><b>${suppliers.length}</b> ${suppliers.length === 1 ? "supplier" : "suppliers"}</div>
  </div>
  <div class="columns">
    ${sections}
  </div>
  <div class="no-print" style="margin-top:30px;text-align:center">
    <button onclick="window.print()" style="padding:8px 18px;font-size:14px;cursor:pointer">Print</button>
  </div>
</body>
</html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 350);
  };

  /**
   * Open the Re-order Report picker: fetches the data once and lets the user
   * pick "All suppliers" or a specific supplier to print.
   */
  const openReorderReportPicker = async () => {
    setReorderLoading(true);
    try {
      const res = await axios.get(`${API}/reports/below-reorder-level`, {
        params: { only_unfilled: true },
      });
      const payload = res.data;
      if (!payload || !payload.suppliers || payload.total_items === 0) {
        toast.info("No products are below reorder level — nothing to print.");
        return;
      }
      setReorderData(payload);
      setShowReorderPicker(true);
    } catch (e) {
      console.error("reorder report fetch failed", e);
      toast.error("Could not load re-order report");
    } finally {
      setReorderLoading(false);
    }
  };

  const handleCompleteCashup = async () => {
    if (!existingCashup) {
      toast.error("No active cash-up to complete");
      return;
    }

    const totalCounted = calculateTotalCounted();
    
    // Build cash count object
    const cashCount = {
      notes: NOTE_DENOMINATIONS.map(d => ({
        denomination: d,
        count: noteCounts[d] || 0,
        total: (noteCounts[d] || 0) * d
      })),
      coins: COIN_DENOMINATIONS.map(d => ({
        denomination: d,
        count: coinCounts[d] || 0,
        total: (coinCounts[d] || 0) * d
      })),
      total_counted: totalCounted
    };

    try {
      const response = await axios.put(`${API}/cashup/${existingCashup.id}/complete`, {
        cash_count: cashCount,
        counted_cash: totalCounted,
        notes: completeNotes,
        completed_by: "ADMIN"
      });
      
      toast.success(`Cash-up completed! Variance: ${formatCurrency(response.data.variance)} (${response.data.variance_status})`);
      setShowCompleteDialog(false);
      setCompleteNotes("");
      fetchSummary();

      // Auto-print the Re-order Report (products at/below reorder level after
      // considering stock_on_order). User asked: this should print
      // automatically when finalizing the cash-up.
      printReorderReport({ silent: true });
    } catch (error) {
      console.error("Error completing cash-up:", error);
      toast.error(error.response?.data?.detail || "Failed to complete cash-up");
    }
  };

  const handleRecalculate = async () => {
    if (!existingCashup) return;
    
    try {
      await axios.post(`${API}/cashup/${existingCashup.id}/recalculate`);
      toast.success("Cash-up recalculated");
      fetchSummary();
    } catch (error) {
      console.error("Error recalculating:", error);
      toast.error("Failed to recalculate");
    }
  };

  const handleVoidCashup = async () => {
    if (!existingCashup || existingCashup.status === "completed") {
      toast.error("Cannot void a completed cash-up");
      return;
    }

    if (!window.confirm("Are you sure you want to void this cash-up?")) return;

    try {
      await axios.delete(`${API}/cashup/${existingCashup.id}`);
      toast.success("Cash-up voided");
      setExistingCashup(null);
      fetchSummary();
    } catch (error) {
      console.error("Error voiding cash-up:", error);
      toast.error(error.response?.data?.detail || "Failed to void cash-up");
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Cash-Up Report - ${selectedDate}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 18px; text-align: center; margin-bottom: 5px; }
            h2 { font-size: 14px; margin-top: 15px; border-bottom: 1px solid #000; }
            .header { text-align: center; margin-bottom: 20px; }
            .row { display: flex; justify-content: space-between; padding: 3px 0; }
            .row.highlight { background: #f0f0f0; font-weight: bold; }
            .section { margin-bottom: 15px; }
            .total-row { border-top: 2px solid #000; font-weight: bold; margin-top: 10px; padding-top: 5px; }
            .variance { font-size: 14px; text-align: center; padding: 10px; margin-top: 20px; }
            .variance.over { background: #d4edda; }
            .variance.short { background: #f8d7da; }
            .variance.balanced { background: #d1ecf1; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const totalCounted = calculateTotalCounted();
  const expectedCash = existingCashup?.summary?.expected_cash || summaryData?.expected_cash || 0;
  const cashVariance = totalCounted - expectedCash;
  
  // Calculate speedpoint/card variance
  const speedpointEntered = dailyForm.speedpointAmount || 0;
  const expectedCard = (summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0);
  const cardVariance = speedpointEntered - expectedCard;
  
  // Total variance combines both cash and card variances
  const totalVariance = cashVariance + cardVariance;
  
  // Keep variance for backward compatibility (some places still use it)
  const variance = totalVariance;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="cashup-title">Cash-Up Procedure</h1>
          <p className="text-gray-500">Terminal: ALL (Combined)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-gray-600">Date:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                const nextDate = e.target.value;
                setSelectedDate(nextDate);

                const parsedDate = new Date(`${nextDate}T00:00:00`);
                if (!Number.isNaN(parsedDate.getTime())) {
                  setSelectedMonth(parsedDate.getMonth() + 1);
                  setSelectedYear(parsedDate.getFullYear());
                }
              }}
              className="w-40"
              data-testid="cashup-date-input"
            />
          </div>
          <Button variant="outline" onClick={() => { fetchHistory(); setShowHistoryDialog(true); }} data-testid="history-btn">
            <History className="h-4 w-4 mr-1" />
            History
          </Button>
          <Button
            variant="outline"
            onClick={openReorderReportPicker}
            disabled={reorderLoading}
            className="text-amber-700 border-amber-300 hover:bg-amber-50"
            data-testid="header-print-reorder-report-btn"
            title="Print the products-below-reorder-level report (all or per supplier)"
          >
            <Printer className="h-4 w-4 mr-1" />
            {reorderLoading ? "Loading…" : "Re-order Report"}
          </Button>
          {!existingCashup && (
            <>
              <Button onClick={handleQuickCashup} className="bg-green-600 hover:bg-green-700" data-testid="quick-cashup-btn">
                <Zap className="h-4 w-4 mr-2" />
                Quick Cash-Up
              </Button>
              <Button onClick={() => setShowStartDialog(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="start-cashup-btn">
                <Calculator className="h-4 w-4 mr-2" />
                Full Cash-Up
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {existingCashup && (
        <Card className={`${existingCashup.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {existingCashup.status === 'completed' ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <Clock className="h-6 w-6 text-amber-600" />
              )}
              <div>
                <p className={`font-semibold ${existingCashup.status === 'completed' ? 'text-green-800' : 'text-amber-800'}`}>
                  {existingCashup.cashup_number && <span className="mr-2">#{existingCashup.cashup_number}</span>}
                  {existingCashup.status === 'completed' ? 'Cash-Up Completed' : 'Cash-Up In Progress'}
                </p>
                <p className="text-sm text-gray-600">
                  {existingCashup.notes === "Quick cash-up (no cash count)" ? "Quick Cash-Up" : "Full Cash-Up"} - {new Date(existingCashup.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => printReorderReport({ silent: false })} data-testid="print-reorder-report-btn">
                <Printer className="h-4 w-4 mr-1" />
                Re-order Report
              </Button>
              {existingCashup.status === 'draft' && (
                <>
                  <Button variant="outline" size="sm" onClick={handleRecalculate} data-testid="recalculate-btn">
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Recalculate
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleVoidCashup} className="text-red-600 hover:text-red-700" data-testid="void-btn">
                    <XCircle className="h-4 w-4 mr-1" />
                    Void
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowEmailDialog(true)} data-testid="email-btn">
                <Mail className="h-4 w-4 mr-1" />
                Email
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
                <Printer className="h-4 w-4 mr-1" />
                Print
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="daily-tab">
            <Calendar className="h-4 w-4" />
            Daily
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="monthly-tab" onClick={() => fetchMonthlyTracking()}>
            <CalendarDays className="h-4 w-4" />
            Monthly
          </TabsTrigger>
        </TabsList>

        {/* Daily Cash-Up Form Tab */}
        <TabsContent value="daily" className="mt-6" key={`daily-tab-${dailyFormKey}`}>
          <DailyFormTab
            selectedDate={selectedDate}
            existingCashupForDate={existingCashupForDate}
            dailyForm={dailyForm}
            setDailyForm={setDailyForm}
            summaryData={summaryData}
            openingFloat={parseFloat(openingFloat) || 0}
            onPrintDaily={handlePrintDaily}
            onFinishClick={() => setShowFinishDialog(true)}
            onSaveDraft={handleSaveDraft}
            savingDraft={savingDraft}
          />
          
          {/* Hidden print content for Daily Form */}
          <div className="hidden">
            <div ref={dailyPrintRef}>
              <div className="header">
                <h1>DAILY CASH-UP</h1>
                <p>Date: {new Date(selectedDate).toLocaleDateString('en-ZA')}</p>
                <p>Cash-Up No: {dailyForm.cashUpNo || '-'}</p>
                <p>Printed: {new Date().toLocaleString()}</p>
              </div>
              <h2>SALES</h2>
              <div className="section">
                <div className="row"><span>Petty Cash:</span><span>R {dailyForm.pettyCash.toFixed(2)}</span></div>
                <div className="row"><span>Cash Sales:</span><span>{formatCurrency(summaryData?.cash_sales)}</span></div>
                <div className="row"><span>Refunds:</span><span>R {dailyForm.refunds.toFixed(2)}</span></div>
                <div className="row"><span>Acc Sales:</span><span>{formatCurrency(summaryData?.account_sales)}</span></div>
                <div className="row"><span>Acc Payment:</span><span>R {dailyForm.accPayment.toFixed(2)}</span></div>
                <div className="row"><span>Card Sales:</span><span>{formatCurrency(summaryData?.card_sales)}</span></div>
                <div className="row highlight"><span>SALES:</span><span>{formatCurrency(summaryData?.total_sales || 0)}</span></div>
              </div>
              <h2>CASH COUNT</h2>
              <div className="section">
                <div className="row"><span>R 200 × {dailyForm.note200}:</span><span>{formatCurrency(dailyForm.note200 * 200)}</span></div>
                <div className="row"><span>R 100 × {dailyForm.note100}:</span><span>{formatCurrency(dailyForm.note100 * 100)}</span></div>
                <div className="row"><span>R 50 × {dailyForm.note50}:</span><span>{formatCurrency(dailyForm.note50 * 50)}</span></div>
                <div className="row"><span>R 20 × {dailyForm.note20}:</span><span>{formatCurrency(dailyForm.note20 * 20)}</span></div>
                <div className="row"><span>R 10 × {dailyForm.note10}:</span><span>{formatCurrency(dailyForm.note10 * 10)}</span></div>
                <div className="row"><span>Change:</span><span>R {dailyForm.change.toFixed(2)}</span></div>
                <div className="row highlight"><span>TOTAL CASH:</span><span>{formatCurrency((dailyForm.note200 * 200) + (dailyForm.note100 * 100) + (dailyForm.note50 * 50) + (dailyForm.note20 * 20) + (dailyForm.note10 * 10) + dailyForm.change)}</span></div>
                <div className="row"><span>Expected Cash:</span><span>{formatCurrency(1000 + (summaryData?.cash_sales || 0) + (summaryData?.account_receipts_cash || 0) - dailyForm.pettyCash - (dailyForm.cashRefunds || 0))}</span></div>
                <div className="row"><span>Cash Balance:</span><span>{formatCurrency(((dailyForm.note200 * 200) + (dailyForm.note100 * 100) + (dailyForm.note50 * 50) + (dailyForm.note20 * 20) + (dailyForm.note10 * 10) + dailyForm.change) - (1000 + (summaryData?.cash_sales || 0) + (summaryData?.account_receipts_cash || 0) - dailyForm.pettyCash - (dailyForm.cashRefunds || 0)))}</span></div>
              </div>
              <h2>SPEEDPOINT</h2>
              <div className="section">
                <div className="row"><span>Batch No:</span><span>{dailyForm.speedpointBatchNo || '-'}</span></div>
                <div className="row"><span>Amount:</span><span>R {dailyForm.speedpointAmount.toFixed(2)}</span></div>
                <div className="row"><span>Expected:</span><span>{formatCurrency((summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0))}</span></div>
                <div className="row"><span>Card Balance:</span><span>{formatCurrency(dailyForm.speedpointAmount - ((summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0)))}</span></div>
              </div>
              <div className="total-row">
                <div className="row highlight"><span>FINAL BALANCE:</span><span>{formatCurrency(
                  (((dailyForm.note200 * 200) + (dailyForm.note100 * 100) + (dailyForm.note50 * 50) + (dailyForm.note20 * 20) + (dailyForm.note10 * 10) + dailyForm.change) - ((summaryData?.cash_sales || 0) + (summaryData?.account_receipts_cash || 0) - dailyForm.pettyCash - (dailyForm.cashRefunds || 0) - (summaryData?.loan_cash_outs || 0) - (summaryData?.withdrawal_cash_outs || 0))) +
                  (dailyForm.speedpointAmount - ((summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0)))
                )}</span></div>
              </div>
            </div>
          </div>
        </TabsContent>

 

        {/* Monthly Cash Tracking Tab */}
        <TabsContent value="monthly" className="mt-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <CalendarDays className="h-6 w-6" />
                  <div>
                    <CardTitle className="text-xl">Monthly Cash Tracking</CardTitle>
                    <p className="text-purple-100 text-sm">
                      {monthlyTracking?.month_name} {selectedYear}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-32 bg-white/20 border-white/30 text-white" data-testid="cashup-monthly-month-select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <SelectItem key={m} value={String(m)}>
                          {new Date(2000, m-1).toLocaleString('en-US', {month: 'long'})}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-24 bg-white/20 border-white/30 text-white" data-testid="cashup-monthly-year-select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={fetchMonthlyTracking}
                    disabled={loadingMonthly}
                    data-testid="refresh-monthly-tracking-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${loadingMonthly ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingMonthly ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              ) : monthlyTracking ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="py-3 px-2 text-center font-bold w-12 border-b">DAY</th>
                        <th className="py-3 px-2 text-center font-bold bg-green-50 border-b">CASH SALES</th>
                        <th className="py-3 px-2 text-center font-bold bg-green-50 border-b">CARD SALES</th>
                        <th className="py-3 px-2 text-center font-bold bg-green-50 border-b">ACC SALES</th>
                        <th className="py-3 px-2 text-center font-bold border-b">TOTAL SALES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyTracking.entries?.map((entry) => {
                        const dayEntry = trackingEntries[entry.day] || {};
                        const cashSales = dayEntry.cash_sales || 0;
                        const cardSales = dayEntry.card_sales || 0;
                        const accSales = dayEntry.acc_sales || 0;
                        const totalSales = cashSales + cardSales + accSales;
                        
                        return (
                          <tr key={entry.day} className="border-b hover:bg-gray-50">
                            <td className="py-1 px-2 text-center font-medium bg-gray-50">{entry.day}</td>
                            <td className="py-1 px-1 bg-green-50">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={cashSales || ''}
                                onChange={(e) => updateTrackingEntry(entry.day, 'cash_sales', e.target.value)}
                                className="h-7 text-right bg-white border-green-200 focus:border-green-500"
                                placeholder="0.00"
                                data-testid={`tracking-cash-${entry.day}`}
                              />
                            </td>
                            <td className="py-1 px-1 bg-green-50">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={cardSales || ''}
                                onChange={(e) => updateTrackingEntry(entry.day, 'card_sales', e.target.value)}
                                className="h-7 text-right bg-white border-green-200 focus:border-green-500"
                                placeholder="0.00"
                                data-testid={`tracking-card-${entry.day}`}
                              />
                            </td>
                            <td className="py-1 px-1 bg-green-50">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={accSales || ''}
                                onChange={(e) => updateTrackingEntry(entry.day, 'acc_sales', e.target.value)}
                                className="h-7 text-right bg-white border-green-200 focus:border-green-500"
                                placeholder="0.00"
                                data-testid={`tracking-acc-${entry.day}`}
                              />
                            </td>
                            <td 
                              className="py-1 px-2 text-right font-bold"
                              style={{ backgroundColor: entry.color?.color || '#E9ECEF' }}
                            >
                              {formatCurrency(totalSales)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-100 font-bold">
                      <tr>
                        <td className="py-3 px-2 text-center">TOTAL</td>
                        <td className="py-3 px-2 text-right bg-green-100">
                          {formatCurrency(Object.values(trackingEntries).reduce((sum, e) => sum + (e?.cash_sales || 0), 0))}
                        </td>
                        <td className="py-3 px-2 text-right bg-green-100">
                          {formatCurrency(Object.values(trackingEntries).reduce((sum, e) => sum + (e?.card_sales || 0), 0))}
                        </td>
                        <td className="py-3 px-2 text-right bg-green-100">
                          {formatCurrency(Object.values(trackingEntries).reduce((sum, e) => sum + (e?.acc_sales || 0), 0))}
                        </td>
                        <td 
                          className="py-3 px-2 text-right text-lg"
                          style={{ backgroundColor: monthlyTracking.totals?.color?.color || '#E9ECEF' }}
                        >
                          {formatCurrency(
                            Object.values(trackingEntries).reduce((sum, e) => 
                              sum + (e?.cash_sales || 0) + (e?.card_sales || 0) + (e?.acc_sales || 0), 0
                            )
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tracking data loaded. Click Refresh to load.</p>
                </div>
              )}
              
              {/* Color Legend & Actions */}
              <div className="border-t p-4">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  {/* Color Legend */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">COLOR LEGEND (Total Sales)</p>
                    <div className="flex flex-wrap gap-1">
                      {monthlyTracking?.color_legend?.map((item, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 text-xs rounded"
                          style={{ backgroundColor: item.color }}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleExportMonthlyTracking}
                      disabled={!monthlyTracking}
                      data-testid="export-monthly-tracking-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export Excel
                    </Button>
                    <Button
                      onClick={handleSaveMonthlyTracking}
                      disabled={savingTracking || !monthlyTracking}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="save-monthly-tracking-btn"
                    >
                      {savingTracking ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cash Flow Tracking Card */}
          <Card className="mt-6">
            <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Wallet className="h-5 w-5" />
                  <div>
                    <CardTitle className="text-lg">Cash Flow (Cash In / Cash Out)</CardTitle>
                    <p className="text-emerald-100 text-sm">Track physical cash movements</p>
                  </div>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={handleSaveCashFlow}
                  disabled={savingCashFlow}
                  data-testid="save-cashflow-btn"
                >
                  {savingCashFlow ? (
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {/* Add New Entry Form */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Add New Entry</p>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Day</Label>
                    <Select 
                      value={String(newCashFlowEntry.day)} 
                      onValueChange={(v) => setNewCashFlowEntry({...newCashFlowEntry, day: parseInt(v)})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: monthlyTracking?.days_in_month || 31}, (_, i) => i + 1).map(d => (
                          <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Reason</Label>
                    <Input
                      type="text"
                      value={newCashFlowEntry.reason}
                      onChange={(e) => setNewCashFlowEntry({...newCashFlowEntry, reason: e.target.value.toUpperCase()})}
                      placeholder="e.g., CASH, SUPPLIER PAYMENT, WITHDRAWAL"
                      className="h-9"
                      data-testid="cashflow-reason-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-green-700">Cash In</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newCashFlowEntry.cash_in || ''}
                      onChange={(e) => setNewCashFlowEntry({...newCashFlowEntry, cash_in: parseFloat(e.target.value) || 0, cash_out: 0})}
                      className="h-9 bg-green-50 border-green-200"
                      placeholder="0.00"
                      data-testid="cashflow-in-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-red-700">Cash Out</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newCashFlowEntry.cash_out || ''}
                      onChange={(e) => setNewCashFlowEntry({...newCashFlowEntry, cash_out: parseFloat(e.target.value) || 0, cash_in: 0})}
                      className="h-9 bg-red-50 border-red-200"
                      placeholder="0.00"
                      data-testid="cashflow-out-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleAddCashFlowEntry} 
                  className="mt-3 bg-emerald-600 hover:bg-emerald-700"
                  size="sm"
                  data-testid="add-cashflow-btn"
                >
                  + Add Entry
                </Button>
              </div>

              {/* Cash Flow Table */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-3 text-left font-bold w-16">DAY</th>
                      <th className="py-2 px-3 text-left font-bold">REASON</th>
                      <th className="py-2 px-3 text-right font-bold bg-green-50 w-32">CASH IN</th>
                      <th className="py-2 px-3 text-right font-bold bg-red-50 w-32">CASH OUT</th>
                      <th className="py-2 px-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashFlowEntries.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-gray-500">
                          No cash flow entries yet. Add your first entry above.
                        </td>
                      </tr>
                    ) : (
                      cashFlowEntries
                        .sort((a, b) => a.day - b.day)
                        .map((entry) => (
                          <tr key={entry.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{entry.day}</td>
                            <td className="py-2 px-3">{entry.reason}</td>
                            <td className="py-2 px-3 text-right bg-green-50 font-medium text-green-700">
                              {entry.cash_in > 0 ? formatCurrency(entry.cash_in) : ''}
                            </td>
                            <td className="py-2 px-3 text-right bg-red-50 font-medium text-red-700">
                              {entry.cash_out > 0 ? formatCurrency(entry.cash_out) : ''}
                            </td>
                            <td className="py-2 px-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCashFlowEntry(entry.id)}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-cashflow-${entry.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                  <tfoot className="bg-gray-100 font-bold">
                    <tr>
                      <td className="py-3 px-3" colSpan={2}>TOTAL</td>
                      <td className="py-3 px-3 text-right bg-green-100 text-green-800">
                        {formatCurrency(cashFlowEntries.reduce((sum, e) => sum + (e.cash_in || 0), 0))}
                      </td>
                      <td className="py-3 px-3 text-right bg-red-100 text-red-800">
                        {formatCurrency(cashFlowEntries.reduce((sum, e) => sum + (e.cash_out || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                    <tr className="bg-emerald-100">
                      <td className="py-3 px-3 font-bold text-emerald-800" colSpan={2}>NET CASH</td>
                      <td 
                        className={`py-3 px-3 text-right text-lg font-bold ${
                          (cashFlowEntries.reduce((sum, e) => sum + (e.cash_in || 0) - (e.cash_out || 0), 0)) >= 0 
                            ? 'text-emerald-800' 
                            : 'text-red-800'
                        }`}
                        colSpan={2}
                      >
                        {formatCurrency(cashFlowEntries.reduce((sum, e) => sum + (e.cash_in || 0) - (e.cash_out || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Cash Count Card (Physical Cash Counting) */}
          <Card className="mt-6">
            <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Banknote className="h-5 w-5" />
                  <div>
                    <CardTitle className="text-lg">Cash Count</CardTitle>
                    <p className="text-amber-100 text-sm">Count physical cash (notes only)</p>
                  </div>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={handleSaveCashCount}
                  disabled={savingCashCount}
                  data-testid="save-cashcount-btn"
                >
                  {savingCashCount ? (
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Cash In Safe & Full Packets */}
                <div className="space-y-4">
                  {/* Cash In Safe Input */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-bold text-blue-800">CASH IN SAFE (Expected)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const netCash = cashFlowEntries.reduce((sum, e) => sum + (e.cash_in || 0) - (e.cash_out || 0), 0);
                          setCashInSafe(netCash);
                          toast.success(`Auto-filled with NET CASH: ${formatCurrency(netCash)}`);
                        }}
                        className="text-xs h-7 bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                        data-testid="auto-fill-cash-btn"
                      >
                        <ArrowDown className="h-3 w-3 mr-1" />
                        Use NET CASH
                      </Button>
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={cashInSafe || ''}
                      onChange={(e) => setCashInSafe(parseFloat(e.target.value) || 0)}
                      className="h-10 text-lg font-bold text-right bg-white"
                      placeholder="0.00"
                      data-testid="cash-in-safe-input"
                    />
                  </div>

                  {/* Full Packets Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-2 font-bold text-sm">
                      FULL PACKETS (10 notes each)
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-2 px-3 text-left">Denomination</th>
                          <th className="py-2 px-3 text-center w-24">Packets</th>
                          <th className="py-2 px-3 text-right w-32">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'r200', label: 'R200', value: 200 },
                          { key: 'r100', label: 'R100', value: 100 },
                          { key: 'r50', label: 'R50', value: 50 },
                          { key: 'r20', label: 'R20', value: 20 },
                          { key: 'r10', label: 'R10', value: 10 },
                        ].map(denom => (
                          <tr key={denom.key} className="border-t">
                            <td className="py-2 px-3 font-medium">{denom.label} x 10</td>
                            <td className="py-1 px-1">
                              <Input
                                type="number"
                                min="0"
                                value={fullPackets[denom.key] || ''}
                                onChange={(e) => setFullPackets({...fullPackets, [denom.key]: parseInt(e.target.value) || 0})}
                                className="h-8 text-center"
                                data-testid={`full-packets-${denom.key}`}
                              />
                            </td>
                            <td className="py-2 px-3 text-right font-medium">
                              {formatCurrency((fullPackets[denom.key] || 0) * denom.value * 10)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 font-bold">
                        <tr>
                          <td className="py-2 px-3" colSpan={2}>PACKETS TOTAL</td>
                          <td className="py-2 px-3 text-right">
                            {formatCurrency(calculateCashCountTotals().fullPacketsTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Right Column - Loose Notes & Summary */}
                <div className="space-y-4">
                  {/* Loose Notes Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-amber-100 px-3 py-2 font-bold text-sm text-amber-800">
                      LOOSE NOTES
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50">
                        <tr>
                          <th className="py-2 px-3 text-left">Denomination</th>
                          <th className="py-2 px-3 text-center w-24">Count</th>
                          <th className="py-2 px-3 text-right w-32">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'r200', label: 'R200', value: 200 },
                          { key: 'r100', label: 'R100', value: 100 },
                          { key: 'r50', label: 'R50', value: 50 },
                          { key: 'r20', label: 'R20', value: 20 },
                          { key: 'r10', label: 'R10', value: 10 },
                        ].map(denom => (
                          <tr key={denom.key} className="border-t bg-amber-50/50">
                            <td className="py-2 px-3 font-medium">{denom.label}</td>
                            <td className="py-1 px-1">
                              <Input
                                type="number"
                                min="0"
                                value={looseNotes[denom.key] || ''}
                                onChange={(e) => setLooseNotes({...looseNotes, [denom.key]: parseInt(e.target.value) || 0})}
                                className="h-8 text-center bg-white"
                                data-testid={`loose-notes-${denom.key}`}
                              />
                            </td>
                            <td className="py-2 px-3 text-right font-medium">
                              {formatCurrency((looseNotes[denom.key] || 0) * denom.value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-amber-100 font-bold">
                        <tr>
                          <td className="py-2 px-3" colSpan={2}>LOOSE TOTAL</td>
                          <td className="py-2 px-3 text-right">
                            {formatCurrency(calculateCashCountTotals().looseNotesTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Summary */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-800 px-3 py-2 font-bold text-sm text-white">
                      SUMMARY
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-gray-600">Full Packets:</span>
                        <span className="font-medium">{formatCurrency(calculateCashCountTotals().fullPacketsTotal)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-gray-600">Loose Notes:</span>
                        <span className="font-medium">{formatCurrency(calculateCashCountTotals().looseNotesTotal)}</span>
                      </div>
                      <div className="flex justify-between py-2 bg-emerald-50 -mx-3 px-3 border-y">
                        <span className="font-bold text-emerald-800">TOTAL COUNTED:</span>
                        <span className="font-bold text-lg text-emerald-800">
                          {formatCurrency(calculateCashCountTotals().totalCounted)}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-gray-600">Cash In Safe:</span>
                        <span className="font-medium">{formatCurrency(cashInSafe)}</span>
                      </div>
                      <div className={`flex justify-between py-2 -mx-3 px-3 rounded ${
                        calculateCashCountTotals().variance >= 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        <span className={`font-bold ${
                          calculateCashCountTotals().variance >= 0 ? 'text-green-800' : 'text-red-800'
                        }`}>VARIANCE:</span>
                        <span className={`font-bold text-lg ${
                          calculateCashCountTotals().variance >= 0 ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {formatCurrency(calculateCashCountTotals().variance)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Financial Summary Card */}
          <Card className="mt-6">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5" />
                  <div>
                    <CardTitle className="text-lg">Monthly Financial Summary</CardTitle>
                    <p className="text-indigo-100 text-sm">Cash Available vs Payments Due</p>
                  </div>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={handleSaveFinancialSummary}
                  disabled={savingFinancial}
                  data-testid="save-financial-btn"
                >
                  {savingFinancial ? (
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Cash Available */}
                <div className="space-y-4">
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-emerald-600 px-3 py-2 font-bold text-sm text-white">
                      CASH AVAILABLE
                    </div>
                    <div className="p-3 space-y-3">
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Cash In Safe:</span>
                        <span className="font-bold text-lg">
                          {formatCurrency(calculateFinancialTotals().cashInSafeAmount)}
                        </span>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Bank Account Balance</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={bankBalance || ''}
                          onChange={(e) => setBankBalance(parseFloat(e.target.value) || 0)}
                          className="mt-1 h-10 text-lg font-bold text-right"
                          placeholder="0.00"
                          data-testid="bank-balance-input"
                        />
                      </div>
                      <div className="flex justify-between items-center py-3 bg-emerald-100 -mx-3 px-3 mt-3">
                        <span className="font-bold text-emerald-800">TOTAL AVAILABLE:</span>
                        <span className="font-bold text-xl text-emerald-800">
                          {formatCurrency(calculateFinancialTotals().totalCashAvailable)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Net Position */}
                  <div className={`p-4 rounded-lg border-2 ${
                    calculateFinancialTotals().netPosition >= 0 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-600 mb-1">NET POSITION</p>
                      <p className={`text-3xl font-bold ${
                        calculateFinancialTotals().netPosition >= 0 
                          ? 'text-green-700' 
                          : 'text-red-700'
                      }`}>
                        {formatCurrency(calculateFinancialTotals().netPosition)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {calculateFinancialTotals().netPosition >= 0 ? 'Surplus' : 'Shortfall'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Payments Due */}
                <div className="space-y-4">
                  {/* Suppliers To Pay - Excel-style Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-red-600 px-3 py-2 font-bold text-sm text-white flex items-center justify-between">
                      <span>SUPPLIERS TO PAY THIS MONTH</span>
                      <span className="text-red-100">{formatCurrency(suppliersToPay.reduce((sum, s) => sum + (s.amount || 0), 0))}</span>
                    </div>
                    <div className="p-2">
                      {/* Table Header */}
                      <div className="grid grid-cols-[1fr_auto_auto] gap-1 text-xs font-semibold text-gray-600 border-b pb-1 mb-1">
                        <span>SUPPLIER</span>
                        <span className="text-right w-24">AMOUNT</span>
                        <span className="w-6"></span>
                      </div>
                      
                      {/* Suppliers List */}
                      <div className="max-h-52 overflow-y-auto space-y-0.5">
                        {suppliersToPay.map(supplier => (
                          <div key={supplier.id} className="grid grid-cols-[1fr_auto_auto] gap-1 items-center py-0.5 hover:bg-gray-50">
                            <span className="text-sm truncate">{supplier.name}</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={supplier.amount || ''}
                              onChange={(e) => {
                                const newAmount = parseFloat(e.target.value) || 0;
                                setSuppliersToPay(prev => prev.map(s => 
                                  s.id === supplier.id ? {...s, amount: newAmount} : s
                                ));
                              }}
                              className="h-7 w-24 text-sm text-right font-mono"
                              data-testid={`supplier-amount-${supplier.id}`}
                            />
                            <button 
                              onClick={() => handleRemoveSupplier(supplier.id)}
                              className="text-gray-400 hover:text-red-500 w-6 flex justify-center"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        
                        {suppliersToPay.length === 0 && (
                          <p className="text-gray-400 text-sm text-center py-3">No suppliers added</p>
                        )}
                      </div>
                      
                      {/* Add New Supplier Row */}
                      <div className="grid grid-cols-[1fr_auto_auto] gap-1 items-center pt-2 mt-1 border-t">
                        <div>
                          <Input
                            type="text"
                            value={newSupplier.name}
                            onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value.toUpperCase()})}
                            placeholder="Add supplier..."
                            className="h-7 text-sm"
                            list="suppliers-datalist"
                            data-testid="new-supplier-name"
                          />
                          <datalist id="suppliers-datalist">
                            {[...suppliersList].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(s => (
                              <option key={s.id} value={s.name} />
                            ))}
                          </datalist>
                        </div>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newSupplier.amount || ''}
                          onChange={(e) => setNewSupplier({...newSupplier, amount: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="h-7 w-24 text-sm text-right font-mono"
                          data-testid="new-supplier-amount"
                        />
                        <Button size="sm" onClick={handleAddSupplier} className="h-7 w-6 p-0 bg-red-600 hover:bg-red-700">
                          +
                        </Button>
                      </div>
                      
                      {/* Total Row */}
                      {suppliersToPay.length > 0 && (
                        <div className="grid grid-cols-[1fr_auto_auto] gap-1 items-center pt-2 mt-2 border-t-2 border-gray-300">
                          <span className="text-sm font-bold">TOTAL</span>
                          <span className="w-24 text-right font-bold font-mono text-sm">
                            {formatCurrency(suppliersToPay.reduce((sum, s) => sum + (s.amount || 0), 0))}
                          </span>
                          <span className="w-6"></span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Monthly Expenses */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-orange-500 px-3 py-2 font-bold text-sm text-white flex items-center justify-between">
                      <span>MONTHLY EXPENSES</span>
                      <span className="text-orange-100">{formatCurrency(calculateFinancialTotals().totalExpenses)}</span>
                    </div>
                    <div className="p-3">
                      {/* Add Expense Form */}
                      <div className="flex gap-2 mb-3">
                        <Input
                          type="text"
                          value={newExpense.name}
                          onChange={(e) => setNewExpense({...newExpense, name: e.target.value.toUpperCase()})}
                          placeholder="Expense name..."
                          className="h-8 flex-1 text-sm"
                          data-testid="new-expense-name"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newExpense.amount || ''}
                          onChange={(e) => setNewExpense({...newExpense, amount: parseFloat(e.target.value) || 0})}
                          placeholder="Amount"
                          className="h-8 w-28 text-sm text-right"
                          data-testid="new-expense-amount"
                        />
                        <Button size="sm" onClick={handleAddExpense} className="h-8 px-2 bg-orange-500 hover:bg-orange-600">
                          +
                        </Button>
                      </div>
                      {/* Expenses List */}
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {monthlyExpenses.length === 0 ? (
                          <p className="text-gray-400 text-sm text-center py-2">No expenses added</p>
                        ) : (
                          monthlyExpenses.map(expense => (
                            <div key={expense.id} className={`flex items-center justify-between py-1 px-2 rounded text-sm ${expense.paid ? 'bg-green-100' : 'bg-orange-50'}`}>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={expense.paid || false}
                                  onChange={() => handleToggleExpensePaid(expense.id)}
                                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                  data-testid={`expense-paid-${expense.id}`}
                                />
                                <span className={`font-medium ${expense.paid ? 'line-through text-gray-500' : ''}`}>{expense.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={expense.paid ? 'line-through text-gray-500' : ''}>{formatCurrency(expense.amount)}</span>
                                {expense.paid && <span className="text-green-600 text-xs font-bold">PAID</span>}
                                <button 
                                  onClick={() => handleRemoveExpense(expense.id)}
                                  className="text-orange-500 hover:text-orange-700"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Total Paid & Total To Pay */}
                  {calculateFinancialTotals().totalPaid > 0 && (
                    <div className="flex justify-between items-center py-2 px-4 bg-green-600 text-white rounded-lg mb-2">
                      <span className="font-bold">TOTAL PAID:</span>
                      <span className="font-bold text-lg">
                        {formatCurrency(calculateFinancialTotals().totalPaid)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 px-4 bg-gray-800 text-white rounded-lg">
                    <span className="font-bold">REMAINING TO PAY:</span>
                    <span className="font-bold text-xl">
                      {formatCurrency(calculateFinancialTotals().totalToPay)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Print Content (Hidden) */}
      <div className="hidden">
        <div ref={printRef}>
          <div className="header">
            <h1>CASH-UP REPORT</h1>
            <p>Date: {selectedDate}</p>
            <p>Terminal: ALL</p>
            <p>Printed: {new Date().toLocaleString()}</p>
          </div>
          
          <h2>SALES SUMMARY</h2>
          <div className="section">
            <div className="row"><span>Total Sales:</span><span>{formatCurrency(summaryData?.total_sales)}</span></div>
            <div className="row"><span>Tax Portion:</span><span>{formatCurrency(summaryData?.tax_portion)}</span></div>
            <div className="row"><span>Nett Sales:</span><span>{formatCurrency(summaryData?.nett_sales)}</span></div>
            <div className="row"><span>Profit:</span><span>{formatCurrency(summaryData?.profit)}</span></div>
            <div className="row"><span>GP%:</span><span>{(summaryData?.gp_percent || 0).toFixed(2)}%</span></div>
          </div>
          
          <h2>PAYMENT METHODS</h2>
          <div className="section">
            <div className="row"><span>Cash Sales:</span><span>{formatCurrency(summaryData?.cash_sales)}</span></div>
            <div className="row"><span>Card Sales:</span><span>{formatCurrency(summaryData?.card_sales)}</span></div>
            <div className="row"><span>Account Sales:</span><span>{formatCurrency(summaryData?.account_sales)}</span></div>
            <div className="row"><span>EFT Sales:</span><span>{formatCurrency(summaryData?.eft_sales)}</span></div>
          </div>
          
          <h2>OTHER</h2>
          <div className="section">
            <div className="row"><span>Account Receipts:</span><span>{formatCurrency(summaryData?.account_receipts)}</span></div>
            <div className="row"><span>Petty Cash:</span><span>-{formatCurrency(summaryData?.petty_cash)}</span></div>
            <div className="row"><span>Discounts:</span><span>-{formatCurrency(summaryData?.discounts)}</span></div>
            <div className="row"><span>Voided Sales:</span><span>{formatCurrency(summaryData?.voided_sales)}</span></div>
            <div className="row"><span>Transactions:</span><span>{summaryData?.transactions || 0}</span></div>
          </div>
          
          <h2>CASH RECONCILIATION</h2>
          <div className="section">
            <div className="row"><span>Expected Cash:</span><span>{formatCurrency(expectedCash)}</span></div>
            <div className="row"><span>Counted Cash:</span><span>{formatCurrency(totalCounted)}</span></div>
            <div className="row total-row"><span>Variance:</span><span>{formatCurrency(variance)}</span></div>
          </div>
          
          <div className={`variance ${variance > 0 ? 'over' : variance < 0 ? 'short' : 'balanced'}`}>
            <strong>{variance > 0 ? 'OVER' : variance < 0 ? 'SHORT' : 'BALANCED'}</strong>
          </div>
        </div>
      </div>

      {/* Start Cash-Up Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Cash-Up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Starting cash-up for: <strong>{selectedDate}</strong>
            </p>
            <div className="space-y-2">
              <Label>Opening Float (Cash in drawer at start)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={openingFloat}
                onChange={(e) => setOpeningFloat(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                data-testid="opening-float-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>Cancel</Button>
            <Button onClick={handleStartCashup} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-start-btn">
              Start Cash-Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Cash-Up Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Cash-Up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Total Counted:</span>
                <span className="font-bold">{formatCurrency(totalCounted)}</span>
              </div>
              <div className="flex justify-between">
                <span>Expected Cash:</span>
                <span className="font-bold">{formatCurrency(expectedCash)}</span>
              </div>
              <div className={`flex justify-between pt-2 border-t ${
                variance > 0 ? 'text-blue-700' : variance < 0 ? 'text-red-700' : 'text-green-700'
              }`}>
                <span className="font-bold">Variance:</span>
                <span className="font-bold">{variance >= 0 ? '+' : ''}{formatCurrency(variance)} ({variance > 0 ? 'OVER' : variance < 0 ? 'SHORT' : 'BALANCED'})</span>
              </div>
            </div>
            
            {variance !== 0 && (
              <div className={`p-3 rounded-lg ${variance < 0 ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`h-5 w-5 ${variance < 0 ? 'text-red-600' : 'text-blue-600'}`} />
                  <span className={`text-sm ${variance < 0 ? 'text-red-700' : 'text-blue-700'}`}>
                    {variance < 0 
                      ? `Cash is SHORT by ${formatCurrency(Math.abs(variance))}`
                      : `Cash is OVER by ${formatCurrency(variance)}`
                    }
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="Add any notes about the cash-up..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
            <Button onClick={handleCompleteCashup} className="bg-green-600 hover:bg-green-700" data-testid="confirm-complete-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Cash-Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between pr-4">
              <DialogTitle>Cash-Up History</DialogTitle>
              <Button
                size="sm"
                variant="outline"
                disabled={fixingHistoric}
                onClick={async () => {
                  if (!window.confirm(
                    "Fix Historic Cash-Up Records?\n\n" +
                    "This will recalculate the 'Expected Card' field on all old cash-ups " +
                    "so account-payment-card amounts are no longer reported as variance. " +
                    "It is safe to run multiple times — already-correct records are skipped."
                  )) return;
                  setFixingHistoric(true);
                  try {
                    const res = await axios.post(`${API}/cashup/daily/backfill-account-receipts`);
                    const { scanned, fixed, already_ok, skipped_no_variance } = res.data;
                    toast.success(
                      `Backfill complete — scanned ${scanned}, fixed ${fixed}, already-ok ${already_ok}` +
                      (skipped_no_variance ? `, skipped ${skipped_no_variance}` : ""),
                      { duration: 7000 }
                    );
                    // Refresh history list so any UI-derived figures pick up changes
                    await fetchHistory?.();
                  } catch (e) {
                    toast.error("Backfill failed: " + (e.response?.data?.detail || e.message));
                  } finally {
                    setFixingHistoric(false);
                  }
                }}
                data-testid="fix-historic-cashups-btn"
                title="Re-runs the variance calculation on historic cash-ups to clear false 'OVER' / 'SHORT' amounts caused by missing account-payment-card data."
              >
                {fixingHistoric ? "Fixing…" : "Fix Historic Records"}
              </Button>
            </div>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {cashupHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No cash-up history found</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">No.</th>
                    <th className="text-right py-2 px-3">Sales</th>
                    <th className="text-right py-2 px-3">Expected</th>
                    <th className="text-right py-2 px-3">Counted</th>
                    <th className="text-right py-2 px-3">Variance</th>
                    <th className="text-center py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cashupHistory.map((cu) => (
                    <tr key={cu.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3">{cu.date}</td>
                      <td className="py-2 px-3 font-medium">{cu.cashup_no}</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(cu.system_data?.total_sales)}</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(cu.calculations?.expected_cash)}</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(cu.calculations?.total_cash_counted)}</td>
                      <td className={`py-2 px-3 text-right font-semibold ${
                        cu.calculations?.final_variance > 0 ? 'text-blue-600' : cu.calculations?.final_variance < 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {cu.calculations?.final_variance >= 0 ? '+' : ''}{formatCurrency(cu.calculations?.final_variance)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/cashup/daily/export/${cu.id}`;
                            link.download = `CashUp_${cu.date}_${cu.cashup_no}.xlsx`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="h-7 text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Excel
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Cash-Up Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Send cash-up report for: <strong>{selectedDate}</strong>
            </p>
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="email@example.com"
                data-testid="email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={emailSending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-email-btn">
              {emailSending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {emailSending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-order Report supplier picker */}
      <Dialog open={showReorderPicker} onOpenChange={setShowReorderPicker}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-amber-700" />
              Re-order Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2 overflow-y-auto" data-testid="reorder-picker-list">
            <p className="text-sm text-gray-600">
              Print for all suppliers (untick any you want to skip), or click a supplier name to print just that one.
            </p>
            <Button
              variant="outline"
              className="w-full justify-between text-amber-800 border-amber-300 hover:bg-amber-50"
              onClick={() => {
                setShowReorderPicker(false);
                printReorderReport({
                  data: reorderData,
                  excludedKeys: reorderExcluded.size > 0 ? reorderExcluded : null,
                });
              }}
              data-testid="reorder-picker-all-btn"
            >
              <span className="flex items-center gap-2">
                <Printer className="h-4 w-4" />
                {reorderExcluded.size > 0 ? `Print ${((reorderData?.suppliers?.length || 0) - reorderExcluded.size)} suppliers` : 'All suppliers'}
              </span>
              <span className="text-xs text-gray-500">
                {(() => {
                  const supList = reorderData?.suppliers || [];
                  const activeSups = supList.filter((s) => !reorderExcluded.has(s.supplier_number || s.supplier_name || "(no-supplier)"));
                  const activeItems = activeSups.reduce((sum, s) => sum + (s.items?.length || 0), 0);
                  return `${activeItems} items · ${activeSups.length} supplier${activeSups.length === 1 ? '' : 's'}`;
                })()}
              </span>
            </Button>
            {reorderExcluded.size > 0 && (
              <button
                type="button"
                className="w-full text-xs text-blue-600 hover:underline py-1"
                onClick={() => setReorderExcluded(new Set())}
                data-testid="reorder-picker-clear-exclusions"
              >
                Clear {reorderExcluded.size} exclusion{reorderExcluded.size === 1 ? '' : 's'}
              </button>
            )}
            <div className="border-t my-2" />
            {(reorderData?.suppliers || []).map((sup) => {
              const key = sup.supplier_number || sup.supplier_name || "(no-supplier)";
              const label = sup.supplier_name || "(No supplier assigned)";
              const sub = sup.supplier_number ? `· ${sup.supplier_number}` : "";
              const excluded = reorderExcluded.has(key);
              return (
                <div
                  key={key}
                  className={`flex items-center gap-2 rounded hover:bg-gray-50 px-1 ${excluded ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 accent-amber-600 cursor-pointer"
                    checked={!excluded}
                    onChange={() => {
                      // Toggle this supplier in the exclusion set — unticked
                      // means "skip in the All-suppliers print". Clicking
                      // the supplier name itself still prints just that
                      // one supplier.
                      setReorderExcluded((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                      });
                    }}
                    aria-label={`${excluded ? 'Include' : 'Exclude'} ${label}`}
                    data-testid={`reorder-picker-exclude-${key}`}
                  />
                  <button
                    type="button"
                    className="flex-1 flex justify-between items-center text-left py-2 px-2 rounded hover:bg-gray-100 min-w-0"
                    onClick={() => {
                      setShowReorderPicker(false);
                      printReorderReport({
                        data: reorderData,
                        supplierKey: sup.supplier_number || sup.supplier_name,
                      });
                    }}
                    data-testid={`reorder-picker-supplier-${key}`}
                  >
                    <span className="text-left truncate min-w-0">
                      <span className="font-medium">{label}</span>
                      {sub && <span className="text-xs text-gray-400 ml-1">{sub}</span>}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                      {sup.items?.length || 0} item{(sup.items?.length || 0) === 1 ? "" : "s"}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReorderPicker(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Monthly Summary Dialog */}
      <Dialog open={showMonthlySummaryDialog} onOpenChange={setShowMonthlySummaryDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Monthly Summary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Month/Year Selector */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Month:</Label>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Year:</Label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <SelectItem key={year} value={String(year)}>{year}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={fetchMonthlySummary} disabled={loadingMonthly}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingMonthly ? 'animate-spin' : ''}`} />
                Load
              </Button>
            </div>

            {loadingMonthly ? (
              <div className="h-48 flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-amber-600" />
              </div>
            ) : monthlySummary ? (
              <>
                {/* Monthly Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-blue-600 font-medium">Total Sales</p>
                      <p className="text-xl font-bold text-blue-800">{formatCurrency(monthlySummary.total_sales)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-green-600 font-medium">Gross Profit</p>
                      <p className="text-xl font-bold text-green-800">{formatCurrency(monthlySummary.profit)}</p>
                      <p className="text-xs text-green-600">{(monthlySummary.gp_percent || 0).toFixed(2)}%</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-red-600 font-medium">Expenses</p>
                      <p className="text-xl font-bold text-red-800">{formatCurrency(monthlySummary.total_expenses)}</p>
                    </CardContent>
                  </Card>
                  <Card className={`${monthlySummary.net_profit >= 0 ? 'bg-emerald-50' : 'bg-red-100'}`}>
                    <CardContent className="p-4 text-center">
                      <p className={`text-xs font-medium ${monthlySummary.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Net Profit</p>
                      <p className={`text-xl font-bold ${monthlySummary.net_profit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>{formatCurrency(monthlySummary.net_profit)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Payment Breakdown */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Card>
                    <CardHeader className="py-2 px-4 bg-gray-50">
                      <CardTitle className="text-sm">Payment Methods</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between"><span>Cash:</span><span className="font-semibold">{formatCurrency(monthlySummary.cash_sales)}</span></div>
                      <div className="flex justify-between"><span>Card:</span><span className="font-semibold">{formatCurrency(monthlySummary.card_sales)}</span></div>
                      <div className="flex justify-between"><span>Account:</span><span className="font-semibold">{formatCurrency(monthlySummary.account_sales)}</span></div>
                      <div className="flex justify-between"><span>EFT:</span><span className="font-semibold">{formatCurrency(monthlySummary.eft_sales)}</span></div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="py-2 px-4 bg-gray-50">
                      <CardTitle className="text-sm">Other Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between"><span>Transactions:</span><span className="font-semibold">{monthlySummary.transactions}</span></div>
                      <div className="flex justify-between"><span>Trading Days:</span><span className="font-semibold">{monthlySummary.trading_days}</span></div>
                      <div className="flex justify-between"><span>Discounts:</span><span className="font-semibold text-red-600">-{formatCurrency(monthlySummary.discounts)}</span></div>
                      <div className="flex justify-between"><span>Supplier Invoices:</span><span className="font-semibold">{formatCurrency(monthlySummary.supplier_invoices)}</span></div>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Breakdown Table */}
                {monthlySummary.daily_breakdown?.length > 0 && (
                  <Card>
                    <CardHeader className="py-2 px-4 bg-gray-50">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Daily Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-3">Date</th>
                              <th className="text-right py-2 px-3">Sales</th>
                              <th className="text-right py-2 px-3">Transactions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthlySummary.daily_breakdown.map((day) => (
                              <tr key={day.date} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">{day.date}</td>
                                <td className="py-2 px-3 text-right font-semibold">{formatCurrency(day.total)}</td>
                                <td className="py-2 px-3 text-right">{day.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-500">
                <p>Select a month and click Load to view summary</p>
              </div>
            )}
          </div>
          
          {/* Hidden print content */}
          <div className="hidden">
            <div ref={monthlyPrintRef}>
              <div className="header">
                <h1>MONTHLY SUMMARY REPORT</h1>
                <p>{monthlySummary?.month_name} {selectedYear}</p>
                <p>Printed: {new Date().toLocaleString()}</p>
              </div>
              <h2>SUMMARY</h2>
              <div className="section">
                <div className="row highlight"><span>Total Sales:</span><span>{formatCurrency(monthlySummary?.total_sales)}</span></div>
                <div className="row"><span>Gross Profit:</span><span>{formatCurrency(monthlySummary?.profit)} ({(monthlySummary?.gp_percent || 0).toFixed(2)}%)</span></div>
                <div className="row"><span>Total Expenses:</span><span>-{formatCurrency(monthlySummary?.total_expenses)}</span></div>
                <div className="row highlight"><span>Net Profit:</span><span>{formatCurrency(monthlySummary?.net_profit)}</span></div>
                <div className="row"><span>Transactions:</span><span>{monthlySummary?.transactions}</span></div>
                <div className="row"><span>Trading Days:</span><span>{monthlySummary?.trading_days}</span></div>
              </div>
              <h2>PAYMENT METHODS</h2>
              <div className="section">
                <div className="row"><span>Cash:</span><span>{formatCurrency(monthlySummary?.cash_sales)}</span></div>
                <div className="row"><span>Card:</span><span>{formatCurrency(monthlySummary?.card_sales)}</span></div>
                <div className="row"><span>Account:</span><span>{formatCurrency(monthlySummary?.account_sales)}</span></div>
                <div className="row"><span>EFT:</span><span>{formatCurrency(monthlySummary?.eft_sales)}</span></div>
              </div>
              {monthlySummary?.daily_breakdown?.length > 0 && (
                <>
                  <h2>DAILY BREAKDOWN</h2>
                  <table>
                    <thead>
                      <tr><th>Date</th><th>Sales</th><th>Transactions</th></tr>
                    </thead>
                    <tbody>
                      {monthlySummary.daily_breakdown.map((day) => (
                        <tr key={day.date}>
                          <td>{day.date}</td>
                          <td style={{textAlign: 'right'}}>{formatCurrency(day.total)}</td>
                          <td style={{textAlign: 'right'}}>{day.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMonthlySummaryDialog(false)}>Close</Button>
            <Button variant="outline" onClick={handlePrintMonthly} disabled={!monthlySummary}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish & Save Dialog */}
      <Dialog open={showFinishDialog} onOpenChange={setShowFinishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-green-600" />
              Finish Daily Cash-Up
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              This will save the daily cash-up for <strong>{selectedDate}</strong> and:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Save to records for history</li>
              <li>Download as Excel file to your computer</li>
              <li>Email report (optional)</li>
            </ul>
            
            {/* Summary Preview */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Cash-Up No:</span>
                <span className="font-semibold">{dailyForm.cashUpNo || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Sales:</span>
                <span className="font-semibold">{formatCurrency((summaryData?.cash_sales || 0) + (summaryData?.card_sales || 0) - dailyForm.refunds)}</span>
              </div>
              <div className="flex justify-between">
                <span>Cash Counted:</span>
                <span className="font-semibold">{formatCurrency((dailyForm.note200 * 200) + (dailyForm.note100 * 100) + (dailyForm.note50 * 50) + (dailyForm.note20 * 20) + (dailyForm.note10 * 10) + dailyForm.change)}</span>
              </div>
              {(() => {
                const totalCounted = (dailyForm.note200 * 200) + (dailyForm.note100 * 100) + (dailyForm.note50 * 50) + (dailyForm.note20 * 20) + (dailyForm.note10 * 10) + dailyForm.change;
                // Use the system's expected_cash - it already includes all calculations
                const expectedCash = summaryData?.expected_cash || 0;
                const cashBalance = totalCounted - expectedCash;
                // Card balance: speedpoint vs expected card sales
                const expectedCard = (summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0);
                const cardBalance = dailyForm.speedpointAmount - expectedCard;
                // Total balance combines both cash and card variances
                const totalBalance = cashBalance + cardBalance;
                
                return (
                  <div className="pt-2 border-t space-y-2">
                    {/* Cash Variance */}
                    <div className={`flex justify-between ${cashBalance < 0 ? 'text-red-700' : cashBalance > 0 ? 'text-blue-700' : 'text-green-700'}`}>
                      <span className="font-medium">Cash Variance:</span>
                      <span className="font-medium">{formatCurrency(cashBalance)} ({cashBalance < 0 ? 'SHORT' : cashBalance > 0 ? 'OVER' : 'OK'})</span>
                    </div>
                    {/* Card Variance */}
                    <div className={`flex justify-between ${cardBalance < 0 ? 'text-red-700' : cardBalance > 0 ? 'text-blue-700' : 'text-green-700'}`}>
                      <span className="font-medium">Card Variance:</span>
                      <span className="font-medium">{formatCurrency(cardBalance)} ({cardBalance < 0 ? 'SHORT' : cardBalance > 0 ? 'OVER' : 'OK'})</span>
                    </div>
                    {/* Total Variance */}
                    <div className={`flex justify-between pt-1 border-t ${totalBalance < 0 ? 'text-red-700 bg-red-50' : totalBalance > 0 ? 'text-blue-700 bg-blue-50' : 'text-green-700 bg-green-50'} -mx-2 px-2 py-1 rounded`}>
                      <span className="font-bold">TOTAL VARIANCE:</span>
                      <span className="font-bold">{formatCurrency(totalBalance)} ({totalBalance < 0 ? 'SHORT' : totalBalance > 0 ? 'OVER' : 'BALANCED'})</span>
                    </div>
                    
                    {/* View Full Breakdown Button */}
                    <button
                      type="button"
                      onClick={() => setShowBreakdown(!showBreakdown)}
                      className="w-full text-xs text-blue-600 hover:text-blue-800 py-1 flex items-center justify-center gap-1"
                      data-testid="view-breakdown-btn"
                    >
                      {showBreakdown ? '▲ Hide' : '▼ View'} Full Breakdown
                    </button>
                    
                    {/* Full Breakdown Details */}
                    {showBreakdown && (
                      <div className="mt-2 p-3 bg-white border rounded-lg text-xs space-y-3">
                        {/* Cash Breakdown */}
                        <div>
                          <h4 className="font-bold text-gray-700 mb-2 border-b pb-1">CASH BREAKDOWN</h4>
                          <table className="w-full">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="text-left py-1 px-2">Denomination</th>
                                <th className="text-center py-1 px-2">Qty</th>
                                <th className="text-right py-1 px-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b">
                                <td className="py-1 px-2">R 200</td>
                                <td className="text-center py-1 px-2">{dailyForm.note200}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.note200 * 200)}</td>
                              </tr>
                              <tr className="border-b">
                                <td className="py-1 px-2">R 100</td>
                                <td className="text-center py-1 px-2">{dailyForm.note100}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.note100 * 100)}</td>
                              </tr>
                              <tr className="border-b">
                                <td className="py-1 px-2">R 50</td>
                                <td className="text-center py-1 px-2">{dailyForm.note50}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.note50 * 50)}</td>
                              </tr>
                              <tr className="border-b">
                                <td className="py-1 px-2">R 20</td>
                                <td className="text-center py-1 px-2">{dailyForm.note20}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.note20 * 20)}</td>
                              </tr>
                              <tr className="border-b">
                                <td className="py-1 px-2">R 10</td>
                                <td className="text-center py-1 px-2">{dailyForm.note10}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.note10 * 10)}</td>
                              </tr>
                              <tr className="border-b">
                                <td className="py-1 px-2">Change/Coins</td>
                                <td className="text-center py-1 px-2">-</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.change)}</td>
                              </tr>
                              <tr className="bg-blue-50 font-bold">
                                <td className="py-1 px-2" colSpan="2">TOTAL COUNTED</td>
                                <td className="text-right py-1 px-2">{formatCurrency(totalCounted)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Expected vs Actual */}
                        <div>
                          <h4 className="font-bold text-gray-700 mb-2 border-b pb-1">EXPECTED vs ACTUAL</h4>
                          <table className="w-full">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="text-left py-1 px-2">Category</th>
                                <th className="text-right py-1 px-2">Expected</th>
                                <th className="text-right py-1 px-2">Actual</th>
                                <th className="text-right py-1 px-2">Variance</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b">
                                <td className="py-1 px-2">Cash</td>
                                <td className="text-right py-1 px-2">{formatCurrency(expectedCash)}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(totalCounted)}</td>
                                <td className={`text-right py-1 px-2 font-medium ${cashBalance < 0 ? 'text-red-600' : cashBalance > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                                  {cashBalance >= 0 ? '+' : ''}{formatCurrency(cashBalance)}
                                </td>
                              </tr>
                              <tr className="border-b">
                                <td className="py-1 px-2">Card (Speedpoint)</td>
                                <td className="text-right py-1 px-2">{formatCurrency(expectedCard)}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(dailyForm.speedpointAmount)}</td>
                                <td className={`text-right py-1 px-2 font-medium ${cardBalance < 0 ? 'text-red-600' : cardBalance > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                                  {cardBalance >= 0 ? '+' : ''}{formatCurrency(cardBalance)}
                                </td>
                              </tr>
                              <tr className="bg-gray-100 font-bold">
                                <td className="py-1 px-2">TOTAL</td>
                                <td className="text-right py-1 px-2">{formatCurrency(expectedCash + expectedCard)}</td>
                                <td className="text-right py-1 px-2">{formatCurrency(totalCounted + dailyForm.speedpointAmount)}</td>
                                <td className={`text-right py-1 px-2 ${totalBalance < 0 ? 'text-red-600' : totalBalance > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                                  {totalBalance >= 0 ? '+' : ''}{formatCurrency(totalBalance)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Cash Calculation Details */}
                        <div>
                          <h4 className="font-bold text-gray-700 mb-2 border-b pb-1">CASH CALCULATION</h4>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span>Opening Float:</span>
                              <span>{formatCurrency(1000)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>+ Cash Sales:</span>
                              <span>{formatCurrency(summaryData?.cash_sales || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>+ Account Receipts (Cash):</span>
                              <span>{formatCurrency(summaryData?.account_receipts_cash || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>- Petty Cash:</span>
                              <span>{formatCurrency(dailyForm.pettyCash)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>- Refunds:</span>
                              <span>{formatCurrency(dailyForm.refunds)}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t pt-1">
                              <span>= Expected Cash:</span>
                              <span>{formatCurrency(expectedCash)}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Card Calculation Details */}
                        <div>
                          <h4 className="font-bold text-gray-700 mb-2 border-b pb-1">CARD CALCULATION</h4>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span>Card Sales:</span>
                              <span>{formatCurrency(summaryData?.card_sales || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>+ Account Receipts (Card):</span>
                              <span>{formatCurrency(summaryData?.account_receipts_card || 0)}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t pt-1">
                              <span>= Expected Card:</span>
                              <span>{formatCurrency(expectedCard)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            
            <div className="space-y-2">
              <Label>Email Report To (optional)</Label>
              <Input
                type="email"
                value={finishEmail}
                onChange={(e) => setFinishEmail(e.target.value)}
                placeholder="email@example.com"
                data-testid="finish-email-input"
              />
              <p className="text-xs text-gray-500">Leave blank to skip email</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinishDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleFinishDailyCashup} 
              disabled={savingDaily}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-finish-btn"
            >
              {savingDaily ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {savingDaily ? "Saving..." : "Save & Download Excel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashUp;
